import { ViewInviteDialogComponent } from './private/profile/details/view-invite/view-invite-dialog.component';
import { LeapModule } from './public/leap/leap.module';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';

let emulatorUrl = environment.isLeapDevelopment
	? environment.devServerUrl
	: 'localhost';

if (
	window.location.hostname === 'localhost' ||
	window.location.hostname === '0.0.0.0'
) {
	if (navigator.platform.substr(0, 3) === 'Mac') {
		emulatorUrl = '127.0.0.1';
	}
}

import { DragDropModule } from '@angular/cdk/drag-drop';
import { ObserversModule } from '@angular/cdk/observers';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule, PercentPipe } from '@angular/common';
import {
	HTTP_INTERCEPTORS,
	provideHttpClient,
	withInterceptorsFromDi,
	withJsonpSupport,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAnalyticsModule } from '@angular/fire/compat/analytics';
import {
	AngularFireAuthModule,
	USE_EMULATOR as USE_AUTH_EMULATOR,
} from '@angular/fire/compat/auth';
import {
	AngularFireDatabaseModule,
	USE_EMULATOR as USE_DATABASE_EMULATOR,
} from '@angular/fire/compat/database';
import {
	SETTINGS as FIRESTORE_SETTINGS,
	USE_EMULATOR as USE_FIRESTORE_EMULATOR,
} from '@angular/fire/compat/firestore';
import {
	AngularFireFunctionsModule,
	REGION,
	USE_EMULATOR as USE_FUNCTIONS_EMULATOR,
} from '@angular/fire/compat/functions';
import {
	AngularFireStorageModule,
	USE_EMULATOR as USE_STORAGE_EMULATOR,
} from '@angular/fire/compat/storage';
import {
	connectFirestoreEmulator,
	initializeFirestore,
	provideFirestore,
} from '@angular/fire/firestore';
import {
	getRemoteConfig,
	provideRemoteConfig,
} from '@angular/fire/remote-config';
import {
	connectStorageEmulator,
	getStorage,
	provideStorage,
} from '@angular/fire/storage';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import {
	MAT_DATE_FORMATS,
	MatNativeDateModule,
	MatRippleModule,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
	BrowserAnimationsModule,
	NoopAnimationsModule,
} from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ComponentsModule } from '@components/components.module';
import { StewartProfileIdsComponent } from '@components/stewart-profile-ids/stewart-profile-ids.component';
import { CoreModule } from '@core/core.module';
import { AtLeastOneRequiredDirective } from '@core/directives/validators/at-least-one-required.directive';
import { PendingChangesGuard } from '@core/guards/pending-changes.guard';
import {
	CentsToDollarsFixedPipe,
	CentsToDollarsPipe,
} from '@core/pipes/cents-to-dollars.pipe';
import { TruncateStringPipe } from '@core/pipes/truncate-string.pipe';
import { PROVIDE_RULE_DEFINITIONS_STATE } from '@domain/rule-definitions';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { NgxsSelectSnapshotModule } from '@ngxs-labs/select-snapshot';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsModule } from '@ngxs/store';
import { ClientSearchService } from '@services/algolia/client-search.service';
import { AuthInterceptor } from '@services/auth/auth.interceptor';
import { SessionService } from '@services/session/session.service';
import { BrokerSearchState } from '@state/algolia-search/broker-search';
import { BrokerageSearchState } from '@state/algolia-search/brokerage-search';
import { ClientSearchState } from '@state/algolia-search/clientSearch';
import { CondoCorporationManagementsSearchState } from '@state/algolia-search/condo-corporation-managements-search';
import { CondoCorporationsSearchState } from '@state/algolia-search/condo-corporations-search';
import { FinancialInstitutionBranchSearchState } from '@state/algolia-search/financial-institution-branch-search';
import { FinancialInstitutionSearchState } from '@state/algolia-search/financial-institution-search';
import { FireInsuranceAgentsSearchState } from '@state/algolia-search/fire-insurance-agents-search';
import { FireInsuranceBrokeragesSearchState } from '@state/algolia-search/fire-insurance-brokerages-search';
import { FireInsuranceBrokersSearchState } from '@state/algolia-search/fire-insurance-brokers-search';
import { FireInsuranceCompaniesSearchState } from '@state/algolia-search/fire-insurance-companies-search';
import { JurisdictionSearchState } from '@state/algolia-search/jurisdiction-search/state';
import { LawFirmSearchState } from '@state/algolia-search/law-firms-search';
import { LawyerSearchState } from '@state/algolia-search/lawyer-search';
import { MatterSearchState } from '@state/algolia-search/matter-search/matter-search.index';
import { MortgageBrokerSearchState } from '@state/algolia-search/mortgage-broker-search';
import { MortgageBrokeragesSearchState } from '@state/algolia-search/mortgage-brokerage-search/state';
import { AppState } from '@state/app/app.state';
import { FeatureFlagState } from '@state/app/applicationDefaults/applicationDefaults.state';
import { AuthState } from '@state/auth/auth.state';
import { BillingBusinessTypesState } from '@state/billing-business';
import { BusinessState } from '@state/business';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessAddressesState } from '@state/business/address';
import { BusinessChecklistsState } from '@state/business/checklists';
import { BusinessDocumentsState } from '@state/business/documents';
import { BusinessFoldersState } from '@state/business/folders/folders.state';
import { LocalBranchesState } from '@state/business/localBranches/localBranches.state';
import { SyncMortgagesInstructionsState } from '@state/business/mortgages-instructions/sync-mortgages-instructions/sync-mortgages-instructions.state';
import { SyncMortgagesPackagesState } from '@state/business/mortgages-instructions/sync-mortgages-packages/sync-mortgages-packages.state';
import { BusinessRequisitionsState } from '@state/business/requisition/business_requisitions.state';
import { StatementOfAccountDefaultsState } from '@state/business/statementOfAccountDefaults';
import { TrustAccountsState } from '@state/business/trustAccounts/trustAccounts.state';
import { BusinessBaseDocxsState } from '@state/business/word/business-base-docxs/business-base-docxs.state';
import { BusinessWordDocumentsState } from '@state/business/word/business-word-documents/business-word-documents.state';
import { GlobalWordDocumentsState } from '@state/business/word/global-word-documents/global-word-documents.state';
import { ClientsState } from '@state/clients';
import { CloserTasksState } from '@state/closer-tasks/closerTasks.state';
import { DashboardState } from '@state/dashboard/dashboard.state';
import { DocumentRequestsState } from '@state/document-requests/document-requests.state';
import { DocumentStylingState } from '@state/document-styling/document-styling.state';
import { BusinessEmailTemplatesState } from '@state/email-templates/business-email-templates/business-email-templates.state';
import { EmailTemplatesState } from '@state/email-templates/email-templates.state';
import { GlobalEmailTemplatesState } from '@state/email-templates/global-email-templates/global-email-templates.state';
import { FileDownloadState } from '@state/file-download/fileDownload.state';
import { FileUploadState } from '@state/file-upload/fileUpload.state';
import { GenericFormsTemplatesState } from '@state/generic-forms';
import { GlobalDocumentsState } from '@state/global-documents';
import { GlobalFinancialInstitutionsState } from '@state/global-financial-institutions/global-financial-institutions.state';
import { GlobalSyncInstitutionsState } from '@state/global-sync-lenders/global-sync-institutions.state';
import { SelectedMatterDocumentsState } from '@state/global/documents/selected-matter-documents.state';
import { SelectedMatterFormsState } from '@state/global/forms/selected-matter-forms.state';
import { LoadingModalState } from '@state/global/loading-modal/loading-modal.state';
import { GlobalStatementOfAccountDefaultsState } from '@state/global/statementOfAccountDefaults/statementOfAccountDefaults.state';
import { TagsState } from '@state/global/tags/tags.state';
import { TitlePlusState } from '@state/global/title-insurance/title-plus/title-plus.state';
import { GoogleSearchState } from '@state/google-search/google-search.state';
import { IdleTimeoutWarningComponent } from '@state/idle-timeout/components/idle-timeout-warning/idle-timeout-warning.component';
import { IdleTimeoutState } from '@state/idle-timeout/idle-timeout.state';
import { InstanceState } from '@state/instance/instance.state';
import { LlcState } from '@state/llc/llc.state';
import { MatterNGXSState } from '@state/matter';
import { MatterAccountingState } from '@state/matter/accounting/accounting.state';
import { AdjustmentsState } from '@state/matter/adjustments';
import { AffidavitState } from '@state/matter/affidavit';
import { MatterChecklistsState } from '@state/matter/checklist';
import { MatterClientIntakeFormState } from '@state/matter/client-intake/client-intake.state';
import { DebtsState } from '@state/matter/debts';
import { DocumentsState } from '@state/matter/documents';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { MatterFoldersState } from '@state/matter/folders/folders.state';
import { MatterFormV2DataState } from '@state/matter/form-data-v2/form-data-v2.state';
import { MatterFormDataState } from '@state/matter/form-data/form-data.state';
import { JurisdictionsState } from '@state/matter/jurisdictions/jurisdictions.state';
import { MatterAllDocumentsAggregatorState } from '@state/matter/matter-all-documents-aggregator/matter-all-documents-aggregator.state';
import { MatterDocumentPackagesAggregatorState } from '@state/matter/matter-document-packages-aggregator/matter-document-packages-aggregator.state';
import { MatterMortgagePackagesState } from '@state/matter/matter-mortgage-packages/matter-mortgage-packages.state';
import { MatterPackagesHistoryState } from '@state/matter/matter-packages-history/matter-packages-history.state';
import { MatterPackagesState } from '@state/matter/matter-packages/matter-packages.state';
import { MatterWordDocumentsState } from '@state/matter/matter-word-documents/matter-word-documents.state';
import { MatterMortgageFormTemplatesState } from '@state/matter/matter_mortgages/matter-mortgage-forms/matter-mortgage-form-templates.state';
import { MatterMortgagesState } from '@state/matter/matter_mortgages/matter-mortgages.state';
import { MatterNotesState } from '@state/matter/notes';
import { MatterRequisitionsState } from '@state/matter/requisitions/matter-requisitions.state';
import { MatterStatementOfAccountsState } from '@state/matter/statementOfAccounts/statementOfAccounts.state';
import { MatterTitlesState } from '@state/matter/titles/titles.state';
import { UndertakingsState } from '@state/matter/undertakings';
import { MatterUploadedFilesState } from '@state/matter/uploaded-files/matter-uploaded-files.state';
import { MatterClientsState } from '@state/matter_clients';
import { MattersListOverviewState } from '@state/matters-list-overview/matters-list-overview.state';
import { LLCMortgageState } from '@state/mortgages/llc-mortgage/llc-mortgage.state';
import { NavigationState } from '@state/navigation/navigation.state';
import { RecentlyViewedState } from '@state/recently-viewed/recently-viewed.state';
import { RemoteConfigState } from '@state/remote-config/state';
import { ReportingBusinessState } from '@state/reporting-business/reporting-business.state';
import { SaleInstrumentsState } from '@state/sale_instruments';
import { SalePinState } from '@state/sale_pins';
import { SaleWritState } from '@state/sale_writ';
import { UIBackdropState } from '@state/ui-backdrop.state.ts/ui-backdrop.state';
import { UserPreferencesState } from '@state/user-preferences/user-preferences.state';
import { UserProfileState } from '@state/user-profile';
import { UserSessionsState } from '@state/user-sessions/user-sessions.state';
import { UserMattersUsageState } from '@state/user-usage-matters/user-usage-matters.state';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import LogRocket from 'logrocket';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { CookieService } from 'ngx-cookie-service';
import { HIGHLIGHT_OPTIONS, HighlightModule } from 'ngx-highlightjs';
import { MarkdownModule } from 'ngx-markdown';
import { NgxPrintModule } from 'ngx-print';
import { AccountingComponent } from 'src/app/private/sale/accounting/accounting.component';
import { CosmolexAccountingItemsTableComponent } from 'src/app/private/sale/accounting/tables/cosmolex-accounting-items-table/cosmolex-accounting-items-table.component';
import { LeapAccountingItemsTableComponent } from 'src/app/private/sale/accounting/tables/leap-accounting-items-table/leap-accounting-items-table.component';
import { SolunoAccountingItemsTableComponent } from 'src/app/private/sale/accounting/tables/soluno-accounting-items-table/soluno-accounting-items-table.component';
import { SolicitorComponent } from 'src/app/private/sale/mortgages/components/solicitor/solicitor.component';
import { envTypes } from '../environments/env.types';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GlodalDataManagementModule } from './global-data-management/global-data-management.module';
import { GlobalDataManagementState } from './global-data-management/state/global-data-management/global-data-management.state';
import { IconsModule } from './icons/icons.module';
import { MaliciousUserPageComponent } from './malicious-user-page/malicious-user-page.component';
import { AppMainComponent } from './private/app-main/app-main.component';
import { LeapCreateMatterComponent } from './private/app-main/global-side-nav/leap-create-matter/leap-create-matter.component';
import { LoadingModalComponent } from './private/app-main/loading-modal/loading-modal.component';
import { NavbarComponent } from './private/app-main/navbar/navbar.component';
import { CreateMatterModule } from './private/create-matter/create-matter.module';
import { AddClientAssociationButtonComponent } from './private/global-profile/associations/add-client-association/button.component';
import { AssociationModalComponent } from './private/global-profile/associations/association-modal/association-modal.component';
import { AssociationComponent } from './private/global-profile/associations/association/association.component';
import { AssociationsComponent } from './private/global-profile/associations/associations.component';
import { BankingComponent } from './private/global-profile/banking/banking.component';
import { ClientBusinessDetailsComponent } from './private/global-profile/details/business/business-details.component';
import { ClientDetailsComponent } from './private/global-profile/details/details.component';
import { ClientTabProfileComponent } from './private/global-profile/details/person/client-tab-profile/client-tab-profile.component';
import { PersonDetailsComponent } from './private/global-profile/details/person/person-details.component';
import { GlobalProfileComponent } from './private/global-profile/global-profile.component';
import { BusinessIdentificationComponent } from './private/global-profile/identification/business/business.component';
import { BaseIdentificationFormComponent } from './private/global-profile/identification/form/base-identification-form/base-identification-form.component';
import { IdentificationFormComponent } from './private/global-profile/identification/form/form.component';
import { IdProofUploadDialogComponent } from './private/global-profile/identification/form/id-proof-upload-dialog/id-proof-upload-dialog.component';
import { ClientIdentificationComponent } from './private/global-profile/identification/identification.component';
import { PersonIdentificationComponent } from './private/global-profile/identification/person/person.component';
import { AddressFormattingComponent } from './private/manage/address-formatting/address-formatting.component';
import { BillingComponent } from './private/manage/billing/billing.component';
import { AddTemplateDialogComponent } from './private/manage/business-checklist/add-template-dialog/add-template-dialog.component';
import { BusinessChecklistComponent } from './private/manage/business-checklist/business-checklist.component';
import { ChecklistContainerComponent } from './private/manage/business-checklist/checklist-container/checklist-container.component';
import { AddressDialogComponent } from './private/manage/business-details/address-dialog/address-dialog.component';
import { BusinessDetailsComponent } from './private/manage/business-details/business-details.component';
import { CurrentUsersComponent } from './private/manage/current-users/current-users.component';
import { DeactivateUserModalComponent } from './private/manage/deactivate-user-modal/deactivate-user-modal.component';
import { AdjustmentDefaultsComponent } from './private/manage/defaults/adjustment-defaults/adjustment-defaults.component';
import { DefaultsComponent } from './private/manage/defaults/defaults.component';
import { EditingDefaultsDialogComponent } from './private/manage/defaults/editing-dialog/editing-defaults-dialog.component';
import { GeneralDefaultsComponent } from './private/manage/defaults/general/general.component';
import { TrustLedgerDefaultsComponent } from './private/manage/defaults/trust-ledger-defaults/trust-ledger-defaults.component';
import { DocxViewerDialogComponent } from './private/manage/documents/docx-templates/docx-viewer-dialog/docx-viewer-dialog.component';
import { BusinessBaseDocxDrawerComponent } from './private/manage/documents/formatting/business-base-docxs-table/business-base-docx-drawer/business-base-docx-drawer.component';
import { BusinessBaseDocxTableComponent } from './private/manage/documents/formatting/business-base-docxs-table/business-base-docx-table.component';
import { DocumentFormattingComponent } from './private/manage/documents/formatting/formatting.component';
import { DocumentStylingComponent } from './private/manage/documents/styling/document-styling.component';
import { LetterheadEditorDialogComponent } from './private/manage/documents/templates/letterhead-editor-dialog';
import { TemplateFolderDeleteDialogComponent } from './private/manage/documents/templates/template-folder-delete-dialog/template-folder-delete-dialog.component';
import { TemplateFolderEditDialogComponent } from './private/manage/documents/templates/template-folder-edit-dialog/template-folder-edit-dialog.component';
import { TemplateMoveDialogComponent } from './private/manage/documents/templates/template-move-dialog/template-move-dialog.component';
import { FooterSelectorComponent } from './private/manage/documents/templates/template-versioning/footer-selector/footer-selector.component';
import { HeaderSelectorComponent } from './private/manage/documents/templates/template-versioning/header-selector/header-selector.component';
import { MatterTypeSelectorComponent } from './private/manage/documents/templates/template-versioning/matter-type-selector/matter-type-selector.component';
import { PendingChangesDialogComponent } from './private/manage/documents/templates/template-versioning/pending-changes-dialog/pending-changes-dialog.component';
import { TemplateVersioningComponent } from './private/manage/documents/templates/template-versioning/template-versioning.component';
import { DocumentsTemplatesComponent } from './private/manage/documents/templates/templates.component';
import { BusinessFinancesGeneralComponent } from './private/manage/finances/business-finances-general/business-finances-general.component';
import { DirectionOfFeeDropdownComponent } from './private/manage/finances/business-finances-general/common/direction-of-fee-dropdown/direction-of-fee-dropdown.component';
import { ESigningGeneralComponent } from './private/manage/finances/e-signing-general/e-signing-general.component';
import { GeneralSettingsComponent } from './private/manage/finances/general-settings/general-settings.component';
import { IdvFinancesGeneralComponent } from './private/manage/finances/idv-general/idv-finances-general/idv-finances-general.component';
import { IdvGeneralComponent } from './private/manage/finances/idv-general/idv-general.component';
import { IdvTreefortVerificationGeneralComponent } from './private/manage/finances/idv-general/idv-treefort-verification-general/idv-treefort-verification-general.component';
import { InstrumentRegistrationSettingsComponent } from './private/manage/finances/instrument-registration-settings/instrument-registration-settings.component';
import { LttSettingsComponent } from './private/manage/finances/ltt-settings/ltt-settings.component';
import { AssystFeeComponent } from './private/manage/finances/mortgages/assyst-fee/assyst-fee.component';
import { DirectionOfAmountComponent } from './private/manage/finances/title-insurance-general/direction-of-amount/direction-of-amount.component';
import { TitleInsuranceGeneralComponent } from './private/manage/finances/title-insurance-general/title-insurance-general.component';
import { TitleSearchSettingsComponent } from './private/manage/finances/title-search-settings/title-search-settings.component';
import { InviteModalComponent } from './private/manage/invites/invite-modal/invite-modal.component';
import { InvitesComponent } from './private/manage/invites/invites.component';
import { ManageComponent } from './private/manage/manage.component';
import { NameFormattingComponent } from './private/manage/name-formatting/name-formatting.component';
import { RequisitionsTemplatesComponent } from './private/manage/requisitions-templates/requisitions-templates.component';
import { SecurityComponent } from './private/manage/security/security.component';
import { AddStatementOfAccountDefaultComponent } from './private/manage/statement-of-account/defaults/add/add.component';
import { StatementOfAccountDefaultsComponent } from './private/manage/statement-of-account/defaults/defaults.component';
import { EditDocumentTextComponent } from './private/manage/statement-of-account/defaults/edit-document-text/edit-document-text.component';
import { LeapManageModalComponent } from './private/manage/statement-of-account/defaults/leap-manage-modal/leap-manage-modal.component';
import { StatementOfAccountDefaultListComponent } from './private/manage/statement-of-account/defaults/list/list.component';
import { SolunoImportModalComponent } from './private/manage/statement-of-account/defaults/soluno-import-modal/soluno-import-modal.component';
import { StatementOfAccountGeneralComponent } from './private/manage/statement-of-account/general/general.component';
import { SetFeeComponent } from './private/manage/statement-of-account/set-fee/set-fee.component';
import { TrustAccountsComponent } from './private/manage/trust-accounts/trust-accounts.component';
import { ProfileDetailsComponent } from './private/profile/details/details.component';
import { SignaturePadComponent } from './private/profile/details/signature-pad/signature-pad.component';
import { ChicagoFirmNameOverrideComponent } from './private/profile/integrations/chicago/chicago-firm-name-override/chicago-firm-name-override.component';
import { ChicagoProfileComponent } from './private/profile/integrations/chicago/chicago.component';
import { ConfirmFirmModalComponent } from './private/profile/integrations/chicago/confirm-firm-modal/confirm-firm-modal.component';
import { CloserSyncEarlyAccessComponent } from './private/profile/integrations/closer-sync/closer-sync-early-access/closer-sync-early-access.component';
import { CloserSyncSignatureRequestModalComponent } from './private/profile/integrations/closer-sync/closer-sync-signature-request-modal/closer-sync-signature-request-modal.component';
import { CloserSyncComponent } from './private/profile/integrations/closer-sync/closer-sync.component';
import { DisconnectAssystComponent } from './private/profile/integrations/closer-sync/drawers/disconnect-assyst/disconnect-assyst.component';
import { CloserSyncGeneralSettingsComponent } from './private/profile/integrations/closer-sync/drawers/settings-drawer/closer-sync-general-settings/closer-sync-general-settings.component';
import { CloserSyncInstitutionSaveModalComponent } from './private/profile/integrations/closer-sync/drawers/settings-drawer/closer-sync-institution-save-modal/closer-sync-institution-save-modal.component';
import { CloserSyncSettingsDrawerComponent } from './private/profile/integrations/closer-sync/drawers/settings-drawer/closer-sync-settings-drawer.component';
import { RequiredListComponent } from './private/profile/integrations/closer-sync/required-list/required-list.component';
import { CloserSyncCongratsConnectedComponent } from './private/profile/integrations/closer-sync/steps/closer-sync-congrats-connected/closer-sync-congrats-connected.component';
import { ConnectMainModalComponent } from './private/profile/integrations/closer-sync/steps/connect-main-modal.component';
import { TermsOfServiceComponent } from './private/profile/integrations/closer-sync/steps/terms-of-service/terms-of-service.component';
import { WelcomeModalComponent } from './private/profile/integrations/closer-sync/welcome-modal/welcome-modal.component';
import { BackToIntegrationsButtonComponent } from './private/profile/integrations/common/back-to-integrations-button/back-to-integrations-button.component';
import { ProfileBenefitsComponent } from './private/profile/integrations/common/benefits/benefits.component';
import { IntegrationCardComponent } from './private/profile/integrations/components/integration-card/integration-card.component';
import { FctIntegrationComponent } from './private/profile/integrations/fct/fct.component';
import { IntegrationsComponent } from './private/profile/integrations/integrations.component';
import { ClientMergeReviewComponent } from './private/profile/integrations/leap/client-merge-review/client-merge-review.component';
import { LeapComponent } from './private/profile/integrations/leap/leap.component';
import { LlcIntegrationComponent } from './private/profile/integrations/llc/llc.component';
import { LLCLoginComponent } from './private/profile/integrations/llc/login/login.component';
import { SignItOnboardingModalComponent } from './private/profile/integrations/sign-it/sign-it-onboarding-modal/sign-it-onboarding-modal.component';
import { SignItComponent } from './private/profile/integrations/sign-it/sign-it.component';
import { StewartProfileComponent } from './private/profile/integrations/stewart/stewart.component';
import { ViewTeranetAccountComponent } from './private/profile/integrations/teranet/credentials/teranet-credentials.component';
import { ProfileTeranetComponent } from './private/profile/integrations/teranet/teranet.component';
import { TeranetUsernameComponent } from './private/profile/integrations/teranet/username/username.component';
import { TitlePlusLsnComponent } from './private/profile/integrations/title-plus/title-plus-lsn/title-plus-lsn.component';
import { TitlePlusProfileComponent } from './private/profile/integrations/title-plus/title-plus-profile.component';
import { TreefortOnboardingModalComponent } from './private/profile/integrations/treefort/treefort-onboarding-modal/treefort-onboarding-modal.component';
import { TreefortPromotionBannerComponent } from './private/profile/integrations/treefort/treefort-promotion-banner/treefort-promotion-banner.component';
import { TreefortProfileComponent } from './private/profile/integrations/treefort/treefort.component';
import { ProfilePreferencesComponent } from './private/profile/preferences/preferences.component';
import { ProfileComponent } from './private/profile/profile.component';
import { CosmolexComponent } from './private/sale/accounting/cosmolex/cosmolex.component';
import { LeapAccountingComponent } from './private/sale/accounting/leap/leap.accounting.component';
import { SolunoComponent } from './private/sale/accounting/soluno/soluno.component';
import { GhostPracticeComponent } from './private/sale/accounting/ghost-practice/ghost-practice.component';
import { GhostPracticeAccountingItemsTableComponent } from './private/sale/accounting/tables/ghost-practice-accounting-items-table/ghost-practice-accounting-items-table.component';
import { LeapExportModalComponent } from './private/sale/accounting/tables/leap-export-modal/leap-export-modal.component';
import { AccountsComponent } from './private/sale/accounts/accounts.component';
import { AddMatterStatementOfAccountComponent } from './private/sale/accounts/add/add.component';
import { MatterStatementOfAccountsEditDocumentTextComponent } from './private/sale/accounts/edit-document-text/edit-document-text.component';
import { StatementOfAccountTableComponent } from './private/sale/accounts/statement-of-account-table/statement-of-account-table.component';
import { AdjustmentsComponent } from './private/sale/adjustments/adjustments.component';
import { CommonExpensesDialogComponent } from './private/sale/adjustments/common-expenses-dialog';
import { DepositDialogComponent } from './private/sale/adjustments/deposit-dialog';
import { HCRADialogComponent } from './private/sale/adjustments/hcra-dialog/hcra-dialog.component';
import { HeatDialogComponent } from './private/sale/adjustments/heat-dialog';
import { HstRebateDialogComponent } from './private/sale/adjustments/hst-rebate-dialog/hst-rebate-dialog.component';
import { HstSalePriceDialogComponent } from './private/sale/adjustments/hst-sale-price-dialog/hst-sale-price-dialog.component';
import { OccupancyFeesDialogComponent } from './private/sale/adjustments/occupancy-fees-dialog/occupancy-fees-dialog.component';
import { OtherDialogComponent } from './private/sale/adjustments/other-dialog/other-dialog.component';
import { PropertyTaxDialogComponent } from './private/sale/adjustments/property-tax-dialog';
import { SalePriceDialogComponent } from './private/sale/adjustments/sale-price-dialog';
import { SalePriceBreakdownComponent } from './private/sale/adjustments/sale-price-dialog/sale-price-breakdown/sale-price-breakdown.component';
import { DepositSideFormComponent } from './private/sale/adjustments/side-form/deposit/deposit-side-form.component';
import { TarrionWarrantyDialogComponent } from './private/sale/adjustments/tarrion-warranty-dialog';
import { TenancyCurrentDialogComponent } from './private/sale/adjustments/tenancy-current-dialog';
import { TenancyPrepaidDialogComponent } from './private/sale/adjustments/tenancy-prepaid-dialog';
import { VacantPropertyDialogComponent } from './private/sale/adjustments/vacant-property-dialog/vacant-property-dialog.component';
import { VtbMortgageDialogComponent } from './private/sale/adjustments/vtb-mortgage-dialog/vtb-mortgage-dialog.component';
import { ApsSideFormComponent } from './private/sale/aps/aps-side-form/aps-side-form.component';
import { VtbMortgageDeleteModalComponent } from './private/sale/aps/aps-side-form/vtb-mortgage-delete-modal/vtb-mortgage-delete-modal.component';
import { ApsComponent } from './private/sale/aps/aps.component';
import { ReviewSimilarPartyComponent } from './private/sale/aps/review-similar-party/review-similar-party.component';
import { ChargeComponent } from './private/sale/closing/charge/charge.component';
import { ChargesComponent } from './private/sale/closing/charges/charges.component';
import { EregFileViewerComponent } from './private/sale/closing/components/ereg-file-viewer/ereg-file-viewer.component';
import { ReviewTransferFieldsComponent } from './private/sale/closing/components/review-transfer-fields/review-transfer-fields.component';
import { ReviewXmlFieldsComponent } from './private/sale/closing/components/review-xml-fields/review-xml-fields.component';
import { TransferComponent } from './private/sale/closing/transfer/transfer.component';
import { UndertakingsComponent } from './private/sale/closing/undertakings';
import { UndertakingDialogComponent } from './private/sale/closing/undertakings/undertaking-dialog';
import { UndertakingItemComponent } from './private/sale/closing/undertakings/undertaking-item/undertaking-item.component';
import { CommissionBrokerageSelectionComponent } from './private/sale/commission/commission-brokerage-selection/commission-brokerage-selection.component';
import { CommissionDetailsComponent } from './private/sale/commission/commission-details/commission-details.component';
import { CommissionStatementFileHandlerComponent } from './private/sale/commission/commission-statement-file-handler/commission-statement-file-handler.component';
import { CommissionComponent } from './private/sale/commission/commission.component';
import { MultipleCommissionsComponent } from './private/sale/commission/multiple-commissions/multiple-commissions.component';
import { CreateNoteComponent } from './private/sale/create-note/create-note.component';
import { DebtsComponent } from './private/sale/debts';
import { DebtDialogComponent } from './private/sale/debts/debt-dialog';
import { AiDocumentProcessingDisclaimerComponent } from './private/sale/documents/ai-document-processing-disclaimer/ai-document-processing-disclaimer.component';
import { AiDocumentProcessingLoadingComponent } from './private/sale/documents/ai-document-processing-loading/ai-document-processing-loading.component';
import { AiScrapingNotificationComponent } from './private/sale/documents/ai-scraping-notification/ai-scraping-notification.component';
import { DocumentComponent } from './private/sale/documents/document/document.component';
import { EditDocumentDrawerComponent } from './private/sale/documents/document/drawer/edit-document-drawer/edit-document-drawer.component';
import { FormViewerComponent } from './private/sale/documents/forms/form-viewer/form-viewer.component';
import { FormsComponent } from './private/sale/documents/forms/forms.component';
import { NoFormsAvailableComponent } from './private/sale/documents/forms/no-forms-available/no-forms-available.component';
import { AddLenderFormModalComponent } from './private/sale/documents/forms/purchase/mortgage-forms/add-lender-form-modal/add-lender-form-modal.component';
import { LenderFormsMortgageCardComponent } from './private/sale/documents/forms/purchase/mortgage-forms/add-lender-form-modal/lender-forms-mortgage-card/lender-forms-mortgage-card.component';
import { ChargeTypeSelectComponent } from './private/sale/documents/forms/purchase/mortgage-forms/charge-type-select/charge-type-select.component';
import { UpdateRequisitionModalComponent } from './private/sale/documents/requisitions/modals/matter-requisition/update-requisition-modal/update-requisition-modal.component';
import { PurchaseRequisitionsComponent } from './private/sale/documents/requisitions/requisitions.component';
import { RequisitionsModule } from './private/sale/documents/requisitions/requisitions.module';
import { ResponseToRequisitionsComponent } from './private/sale/documents/response-to-requisitions/response-to-requisitions.component';
import { ContextDialogComponent } from './private/sale/documents/sale-docs/context-dialog/context-dialog.component';
import { DocumentBlockComponent } from './private/sale/documents/sale-docs/document-block/document-block.component';
import { DocumentDeleteDialogComponent } from './private/sale/documents/sale-docs/document-delete-dialog/document-delete-dialog.component';
import { FolderDeleteDialogComponent } from './private/sale/documents/sale-docs/folder-delete-dialog/folder-delete-dialog.component';
import { FolderEditDialogComponent } from './private/sale/documents/sale-docs/folder-edit-dialog/folder-edit-dialog.component';
import { FolderMoveDialogComponent } from './private/sale/documents/sale-docs/folder-move-dialog/folder-move-dialog.component';
import { ManageTemplatesContactAdminsComponent } from './private/sale/documents/sale-docs/manage-templates-contact-admins/manage-templates-contact-admins.component';
import { MergeDocumentDialogComponent } from './private/sale/documents/sale-docs/merge-document-dialog';
import { NewDocumentDialogComponent } from './private/sale/documents/sale-docs/new-document-dialog';
import { SaleDocsComponent } from './private/sale/documents/sale-docs/sale-docs.component';
import { TemplateSidebarComponent } from './private/sale/documents/sale-docs/template-sidebar/template-sidebar.component';
import { UploadedDeleteModalComponent } from './private/sale/documents/uploaded/delete-modal/delete-modal.component';
import { UploadedFileEditModalComponent } from './private/sale/documents/uploaded/edit-modal/edit-modal.component';
import { UploadDocumentModalComponent } from './private/sale/documents/uploaded/modal/modal.component';
import { UploadedDocumentsComponent } from './private/sale/documents/uploaded/uploaded.component';
import { CreateEncumbranceComponent } from './private/sale/encumbrances/create-encumbrance/create-encumbrance.component';
import { ComputershareBranchSelectComponent } from './private/sale/encumbrances/encumbrance/components/institution/computershare-branch-select/computershare-branch-select.component';
import { ContactInfoFormComponent } from './private/sale/encumbrances/encumbrance/components/institution/contact-info-form/contact-info-form.component';
import { CreateInstitutionBranchComponent } from './private/sale/encumbrances/encumbrance/components/institution/create-branch/create-branch.component';
import { CreateInstitutionComponent } from './private/sale/encumbrances/encumbrance/components/institution/create-institution/create-institution.component';
import { EncumbranceInstitutionComponent } from './private/sale/encumbrances/encumbrance/components/institution/institution.component';
import { MortgageDetailsComponent } from './private/sale/encumbrances/encumbrance/components/mortgage-details/mortgage-details.component';
import { EncumbranceMortgageeComponent } from './private/sale/encumbrances/encumbrance/components/mortgagee/mortgagee.component';
import { EncumbrancePrivateLenderComponent } from './private/sale/encumbrances/encumbrance/components/private-lender/private-lender.component';
import { EncumbranceSideFormComponent } from './private/sale/encumbrances/encumbrance/components/side-form/encumbrance-side-form.component';
import { PaymentCalculatorSideFormComponent } from './private/sale/encumbrances/encumbrance/components/side-form/payment-calculator/payment-calculator-side-form.component';
import { EncumbranceComponent } from './private/sale/encumbrances/encumbrance/encumbrance.component';
import { EncumbrancesComponent } from './private/sale/encumbrances/encumbrances.component';
import { CustomStatementComponent } from './private/sale/execution-affidavits/custom-statement/custom-statement.component';
import { ExecutionAffidavitsComponent } from './private/sale/execution-affidavits/execution-affidavits.component';
import { ImportDetailsComponent } from './private/sale/execution-affidavits/import-details/import-details.component';
import { AgreementDateComponent } from './private/sale/fields/agreement-date/agreement-date.component';
import { BrokerComponent } from './private/sale/fields/brokerage/broker/broker.component';
import { CreateBrokerComponent } from './private/sale/fields/brokerage/broker/create/create-broker.component';
import { EditBrokerSideFormComponent } from './private/sale/fields/brokerage/broker/side-form/broker-edit/broker-edit-side-form.component';
import { BrokerSideFormComponent } from './private/sale/fields/brokerage/broker/side-form/broker-side-form.component';
import { BrokerageSearchComponent } from './private/sale/fields/brokerage/brokerage-search/search.component';
import { BrokerageComponent } from './private/sale/fields/brokerage/brokerage.component';
import { CreateBrokerageComponent } from './private/sale/fields/brokerage/create/create.component';
import { BrokerageSearchSideFormInputComponent } from './private/sale/fields/brokerage/side-form/brokerage-search/search.component';
import { BrokerageSideFormComponent } from './private/sale/fields/brokerage/side-form/brokerage-side-form.component';
import { CreateBrokerageSideFormComponent } from './private/sale/fields/brokerage/side-form/create/create-brokerage-side-form.component';
import { EditBrokerageSideFormComponent } from './private/sale/fields/brokerage/side-form/edit/edit-brokerage-side-form.component';
import { ClientsSearchAndCreateComponent } from './private/sale/fields/client/clients-search-and-create/clients-search-and-create.component';
import { CreateBusinessClientComponent } from './private/sale/fields/client/create/create-business-client/create-business-client.component';
import { CreateMiniClientComponent } from './private/sale/fields/client/create/create-client-mini.component';
import { CreatePersonClientComponent } from './private/sale/fields/client/create/create-person-client/create-person-client.component';
import { CreateClientMiniModalComponent } from './private/sale/fields/client/create/modal/modal.component';
import { CreateBusinessClientSideFormComponent } from './private/sale/fields/client/create/side-form/create-business-client-side-form/create-business-client-side-form.component';
import { CreateClientSideFormComponent } from './private/sale/fields/client/create/side-form/create-client-side-form.component';
import { CreatePersonClientSideFormComponent } from './private/sale/fields/client/create/side-form/create-person-client-side-form/create-person-client-side-form.component';
import { EditBusinessClientComponent } from './private/sale/fields/client/simple-edit/edit-business-client/edit-business-client.component';
import { EditClientComponent } from './private/sale/fields/client/simple-edit/edit-client.component';
import { EditClientModalComponent } from './private/sale/fields/client/simple-edit/edit-modal/edit-modal.component';
import { EditPersonClientComponent } from './private/sale/fields/client/simple-edit/edit-person-client/edit-person-client.component';
import { EditBusinessClientSideFormComponent } from './private/sale/fields/client/simple-edit/side-form/edit-client/edit-business-client-side-form/edit-business-client-side-form.component';
import { EditClientSideFormComponent } from './private/sale/fields/client/simple-edit/side-form/edit-client/edit-client-side-form.component';
import { EditPersonClientSideFormComponent } from './private/sale/fields/client/simple-edit/side-form/edit-client/edit-person-client-side-form/edit-person-client-side-form.component';
import { ClosingDateComponent } from './private/sale/fields/closing-date/closing-date.component';
import { CondoCorpSearchCardComponent } from './private/sale/fields/condo/condo-corp-search-card/condo-corp-search-card.component';
import { CondoManagementTypeComponent } from './private/sale/fields/condo/condo-management-type/condo-management-type.component';
import { CondoMgmtSearchCardComponent } from './private/sale/fields/condo/condo-mgmt-search-card/condo-mgmt-search-card.component';
import { CondoComponent } from './private/sale/fields/condo/condo.component';
import { DepositHolderDropdownComponent } from './private/sale/fields/deposit-holder-dropdown/deposit-holder-dropdown.component';
import { DepositHolderComponent } from './private/sale/fields/deposit-holder/deposit-holder.component';
import { DepositDrawerComponent } from './private/sale/fields/deposit/deposit-drawer/deposit-drawer.component';
import { DepositComponent } from './private/sale/fields/deposit/deposit.component';
import { SaleDepositSideFormComponent } from './private/sale/fields/deposit/side-form/sale-deposit-side-form.component';
import { InterestEstateComponent } from './private/sale/fields/interest-estate/interest-estate.component';
import { JurisdictionFieldComponent } from './private/sale/fields/jurisdiction-field/jurisdiction-field.component';
import { ClerkComponent } from './private/sale/fields/law-firms/clerk/clerk.component';
import { CreateLawFirmEmployeeComponent } from './private/sale/fields/law-firms/create-employee/create-employee.component';
import { CreateLawFirmComponent } from './private/sale/fields/law-firms/create/create.component';
import { LawFirmsComponent } from './private/sale/fields/law-firms/law-firms.component';
import { LawyerComponent } from './private/sale/fields/law-firms/lawyer/lawyer.component';
import { LawFirmSearchComponent } from './private/sale/fields/law-firms/search/search.component';
import { CreateLawFirmSideFormComponent } from './private/sale/fields/law-firms/side-form/create/law-firms-create-side-form.component';
import { EditLawFirmSideFormComponent } from './private/sale/fields/law-firms/side-form/edit/law-firms-edit-side-form.component';
import { LawFirmsSideFormComponent } from './private/sale/fields/law-firms/side-form/law-firms-side-form.component';
import { EditLawyerSideFormComponent } from './private/sale/fields/law-firms/side-form/lawyer/edit/lawyer-edit-side-form.component';
import { LawyerSideFormComponent } from './private/sale/fields/law-firms/side-form/lawyer/lawyer-side-form.component';
import { LawFirmSearchSideFormComponent } from './private/sale/fields/law-firms/side-form/search/search-firm-side-form.component';
import { LroComponent } from './private/sale/fields/lro/lro.component';
import { LROSuggestionComponent } from './private/sale/fields/lro/suggestion/suggestion.component';
import { MatterAddressComponent } from './private/sale/fields/matter-address/address.component';
import { OccupancyDateComponent } from './private/sale/fields/occupancy-date/occupancy-date.component';
import { ParcelOfTiedLandFormComponent } from './private/sale/fields/property-type/parcel-of-tied-land-form/parcel-of-tied-land-form.component';
import { ParcelOfTiedLandOverviewComponent } from './private/sale/fields/property-type/parcel-of-tied-land-overview/parcel-of-tied-land-overview.component';
import { PropertyTypeSelectionComponent } from './private/sale/fields/property-type/property-type-selection/property-type-selection.component';
import { PropertyTypeComponent } from './private/sale/fields/property-type/property-type.component';
import { SalePriceComponent } from './private/sale/fields/sale-price/sale-price.component';
import { TitleSearchCompletionDateComponent } from './private/sale/fields/title-search-completion-date/title-search-completion-date.component';
import { HistoryComponent } from './private/sale/history/history.component';
import { ContactsComponent } from './private/sale/home/contacts/contacts.component';
import { EditMatterDetailsComponent } from './private/sale/home/edit-matter-details/edit-matter-details.component';
import { SaleHomeComponent } from './private/sale/home/home.component';
import { KeyDatesModalComponent } from './private/sale/home/key-dates-modal/key-dates-modal.component';
import { KeysModalComponent } from './private/sale/home/keys-modal/keys-modal.component';
import { SaleLocationComponent } from './private/sale/home/location/location.component';
import { MatterChecklistEmptyStateComponent } from './private/sale/home/matter-checklist/matter-checklist-empty-state/matter-checklist-empty-state.component';
import { MatterChecklistStageComponent } from './private/sale/home/matter-checklist/matter-checklist-stage/matter-checklist-stage.component';
import { MatterChecklistComponent } from './private/sale/home/matter-checklist/matter-checklist.component';
import { NoteComponent } from './private/sale/home/note/note.component';
import { RightSideComponent } from './private/sale/home/right-side/right-side.component';
import { LastOpenedTabsComponent } from './private/sale/last-opened-tabs/last-opened-tabs.component';
import { EditGeneratedTitleComponent } from './private/sale/ledger/edit-generated-title/edit-generated-title.component';
import { LedgerInstructionsModalComponent } from './private/sale/ledger/ledger-instructions-modal/ledger-instructions-modal.component';
import { LedgerComponent } from './private/sale/ledger/ledger.component';
import { MortgageDrawerComponent } from './private/sale/ledger/mortgage-drawer/mortgage-drawer.component';
import { PaymentReceiptModalComponent } from './private/sale/ledger/payment-receipt-modal/payment-receipt-modal.component';
import { SalePriceItemDrawerComponent } from './private/sale/ledger/sale-price-item-drawer/sale-price-item-drawer.component';
import { SalePriceItemizationComponent } from './private/sale/ledger/sale-price-item-drawer/sale-price-itemization/sale-price-itemization.component';
import { LttComponent } from './private/sale/ltt/ltt.component';
import { LTTSettingsDialogComponent } from './private/sale/ltt/lttsettings/lttsettings-dialog.component';
import { MatterActivityComponent } from './private/sale/matter-activity/matter-activity.component';
import { AddMortgageComponent } from './private/sale/mortgages/add-mortgage/add-mortgage.component';
import { CloserSyncMortgageComponent } from './private/sale/mortgages/closer-sync/closer-sync-mortgage/closer-sync-mortgage.component';
import { CloserSyncDocumentsComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-documents/closer-sync-documents.component';
import { CloserSyncGeneralDetailsComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-general-details/closer-sync-general-details.component';
import { CloserSyncLenderDocsComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-lender-docs/closer-sync-lender-docs.component';
import { CloserSyncLendersComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-lenders/closer-sync-lenders.component';
import { CloserSyncMortgageDetailsComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-mortgage-details/closer-sync-mortgage-details.component';
import { CloserSyncPromptsComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-prompts/closer-sync-prompts.component';
import { CloserSyncReportingComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-reporting/closer-sync-reporting.component';
import { CloserSyncTermsComponent } from './private/sale/mortgages/closer-sync/components/closer-sync-terms/closer-sync-terms.component';
import { RequiredPinModalComponent } from './private/sale/mortgages/closer-sync/required-pin-modal/required-pin-modal.component';
import { CorrespondenceCardComponent } from './private/sale/mortgages/components/correspondence-card/correspondence-card.component';
import { GuarantorsComponent } from './private/sale/mortgages/components/guarantors/guarantors.component';
import { MortgageBrokerModalComponent } from './private/sale/mortgages/components/mortgage-broker-modal/mortgage-broker-modal.component';
import { MortgageBrokerComponent } from './private/sale/mortgages/components/mortgage-broker/mortgage-broker.component';
import { MortgageBrokerageModalComponent } from './private/sale/mortgages/components/mortgage-brokerage-modal/mortgage-brokerage-modal.component';
import { MortgageHeaderComponent } from './private/sale/mortgages/components/mortgage-header/mortgage-header.component';
import { NetAmountComponent } from './private/sale/mortgages/components/net-amount/net-amount.component';
import { MatterMortgagePaymentsComponent } from './private/sale/mortgages/components/payments/payments.component';
import { PriorityWarningComponent } from './private/sale/mortgages/components/priority-warning/priority-warning.component';
import { LLCReportComponent } from './private/sale/mortgages/components/report/report.component';
import { SearchMortgageBrokerComponent } from './private/sale/mortgages/components/search-mortgage-broker/search-mortgage-broker.component';
import { SearchMortgageBrokerageComponent } from './private/sale/mortgages/components/search-mortgage-brokerage/search-mortgage-brokerage.component';
import { LLCAmendmentsModalComponent } from './private/sale/mortgages/llc/components/amendments/amendments.component';
import { SyncAmendmentUsersComponent } from './private/sale/mortgages/llc/components/amendments/sync-amendment-users/sync-amendment-users.component';
import { TdAcknowledgeModalComponent } from './private/sale/mortgages/llc/components/amendments/td-acknowledge-modal/td-acknowledge-modal.component';
import { LLCContactCardComponent } from './private/sale/mortgages/llc/components/contact-card/contact-card.component';
import { LlcFileDownloadStatusComponent } from './private/sale/mortgages/llc/components/download-documents/components/llc-file-download-status/llc-file-download-status.component';
import { DownloadDocumentsComponent } from './private/sale/mortgages/llc/components/download-documents/download-documents.component';
import { LLCLenderDetailsComponent } from './private/sale/mortgages/llc/components/lender-details/lender-details.component';
import { LlcDownloadsTableComponent } from './private/sale/mortgages/llc/components/llc-downloads-table/llc-downloads-table.component';
import { LlcStepperComponent } from './private/sale/mortgages/llc/components/llc-stepper/llc-stepper.component';
import { LLCMortgageDetailsComponent } from './private/sale/mortgages/llc/components/mortgage-details/mortgage-details.component';
import { PortalIframeComponent } from './private/sale/mortgages/llc/components/portal-iframe/portal-iframe.component';
import { ResourcesComponent } from './private/sale/mortgages/llc/components/resources/resources.component';
import { LlcMatterMortgageComponent } from './private/sale/mortgages/llc/llc.component';
import { LLCExistingMatterModalComponent } from './private/sale/mortgages/llc/sync/existing-matter/existing-matter.component';
import { PurchasePriceComponent } from './private/sale/mortgages/llc/sync/existing-matter/purchase-price/purchase-price.component';
import { SyncDataComponent } from './private/sale/mortgages/llc/sync/existing-matter/sync-data/sync-data.component';
import { LLCNewMatterModalComponent } from './private/sale/mortgages/llc/sync/new-matter/new-matter.component';
import { ChangePriorityComponent } from './private/sale/mortgages/manual/components/change-priority/change-priority.component';
import { ChargeTypeDropdownComponent } from './private/sale/mortgages/manual/components/charge-type-dropdown/charge-type-dropdown.component';
import { DeleteDocumentModalComponent } from './private/sale/mortgages/manual/components/delete-document-modal/delete-document-modal.component';
import { EditCategoryModalComponent } from './private/sale/mortgages/manual/components/edit-category-modal/edit-category-modal.component';
import { EditingMortgageReLineDialogComponent } from './private/sale/mortgages/manual/components/editing-dialog/editing-mortgage-reline-dialog.component';
import { ManualMortgageLenderDetailsComponent } from './private/sale/mortgages/manual/components/lender-details/lender-details.component';
import { LenderPercentageOfInterestComponent } from './private/sale/mortgages/manual/components/lender-details/lender-percentage-of-interest/lender-percentage-of-interest.component';
import { LoanAmountComponent } from './private/sale/mortgages/manual/components/loan-amount/loan-amount.component';
import { ManualMortgageDetailsComponent } from './private/sale/mortgages/manual/components/mortgage-details/mortgage-details.component';
import { ManualMortgageTermsComponent } from './private/sale/mortgages/manual/components/mortgage-terms/mortgage-terms.component';
import { ManualMortgageComponent } from './private/sale/mortgages/manual/manual.component';
import { MatterMortgageComponent } from './private/sale/mortgages/mortgage/mortgage.component';
import { MatterMortgagesComponent } from './private/sale/mortgages/mortgages.component';
import { MortgagesModule } from './private/sale/mortgages/mortgages.module';
import { IntakeCancelModalComponent } from './private/sale/mortgages/stepper/intake-cancel-modal/intake-cancel-modal.component';
import { MortgageIntakeFieldsComponent } from './private/sale/mortgages/stepper/mortgage-intake-fields/mortgage-intake-fields.component';
import { MortgageIntakeComponent } from './private/sale/mortgages/stepper/mortgage-intake.component';
import { IntakeDropdownComponent } from './private/sale/parties/client-intake-page/components/intake-dropdown/intake-dropdown.component';
import { PartiesClientsComponent } from './private/sale/parties/clients/clients.component';
import { PartyCapacityComponent } from './private/sale/parties/components/capacity/capacity.component';
import { ClientIntakeRequestComponent } from './private/sale/parties/components/client-intake-request/client-intake-request.component';
import { ClientTabComponent } from './private/sale/parties/components/client-tab/client-tab.component';
import { EditingSalutationsReLinesDialogComponent } from './private/sale/parties/components/editing-dialog/editing-salutations-relines-dialog.component';
import { FileSalutationsComponent } from './private/sale/parties/components/file-salutations/file-salutations.component';
import { PartyClientsHeaderComponent } from './private/sale/parties/components/party-clients-header/party-clients-header.component';
import { IdvRequestModalComponent } from './private/sale/parties/idv-modal/idv-modal.component';
import { IdvTableComponent } from './private/sale/parties/idv-modal/idv-table/idv-table.component';
import { CancelRequestsComponent } from './private/sale/parties/idv-modal/steps/cancel-requests/cancel-requests.component';
import { ClientsSelectComponent } from './private/sale/parties/idv-modal/steps/clients-select/clients-select.component';
import { SendRequestsComponent } from './private/sale/parties/idv-modal/steps/send-requests/send-requests.component';
import { IdvReviewModalComponent } from './private/sale/parties/idv-review-modal/idv-review-modal.component';
import { ConfirmDetailsComponent } from './private/sale/parties/idv-review-modal/steps/confirm-details/confirm-details.component';
import { ReviewReportComponent } from './private/sale/parties/idv-review-modal/steps/review-report/review-report.component';
import { IdDetailsComponent } from './private/sale/parties/idv-review-modal/steps/sync-id-data/id-details/id-details.component';
import { SyncIdDataComponent } from './private/sale/parties/idv-review-modal/steps/sync-id-data/sync-id-data.component';
import { PartiesMortgagorsComponent } from './private/sale/parties/mortgagors/mortgagors.component';
import { PartiesOtherPartiesComponent } from './private/sale/parties/other-parties/other-parties.component';
import { PartiesComponent } from './private/sale/parties/parties.component';
import { ComplianceLettersComponent } from './private/sale/property/compliance-letters/compliance-letters.component';
import { PropertyCondoCorpComponent } from './private/sale/property/condo-corp/condo-corp.component';
import { CondoCorporationAttentionContactModalComponent } from './private/sale/property/condo-corp/condo-corporation-attention-modal/condo-corporation-attention-contact-modal.component';
import { CondoCorporationModalComponent } from './private/sale/property/condo-corp/condo-corporation-modal/condo-corporation-modal.component';
import { DeleteConfirmationModalComponent } from './private/sale/property/condo-corp/delete-confirmation-modal/delete-confirmation-modal.component';
import { SearchCondoCorporationComponent } from './private/sale/property/condo-corp/search-condo-corporation/search-condo-corporation.component';
import { SearchManagementCompanyComponent } from './private/sale/property/condo-corp/search-management-company/search-management-company.component';
import { PropertyUnitOverviewComponent } from './private/sale/property/details/components/property-unit-overview/property-unit-overview.component';
import { PropertyUnitsModalComponent } from './private/sale/property/details/components/property-units-modal/property-units-modal.component';
import { ValueAndLtvComponent } from './private/sale/property/details/components/value-and-ltv/value-and-ltv.component';
import { PropertyDetailsComponent } from './private/sale/property/details/details.component';
import { DeleteJurisdictionConfirmationDialogComponent } from './private/sale/property/details/dialogs/delete-jurisdiction-confirmation-dialog/delete-jurisdiction-confirmation-dialog.component';
import { FireInsuranceComponent } from './private/sale/property/fire-insurance/fire-insurance.component';
import { SearchFireInsuranceAgentComponent } from './private/sale/property/fire-insurance/search-fire-insurance-agent/search-fire-insurance-agent.component';
import { SearchFireInsuranceBrokerComponent } from './private/sale/property/fire-insurance/search-fire-insurance-broker/search-fire-insurance-broker.component';
import { SearchFireInsuranceBrokerageComponent } from './private/sale/property/fire-insurance/search-fire-insurance-brokerage/search-fire-insurance-brokerage.component';
import { SearchFIreInsuranceCompanyComponent } from './private/sale/property/fire-insurance/search-fire-insurance-company/search-fire-insurance-company.component';
import { CreateFireInsuranceBrokerageComponent } from './private/sale/property/fire-insurance/side-form/create/fire-insurance-brokerage/create-fire-insurance-brokerage.component';
import { CreateFireInsuranceCompanyComponent } from './private/sale/property/fire-insurance/side-form/create/fire-insurance-company/create-fire-insurance-company.component';
import { EditFireInsuranceAgentComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-agent/edit-fire-insurance-agent.component';
import { EditFireInsuranceBrokerComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-broker/edit-fire-insurance-broker.component';
import { EditFireInsuranceBrokerageComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-brokerage/edit-fire-insurance-brokerage.component';
import { EditFireInsuranceCompanyComponent } from './private/sale/property/fire-insurance/side-form/edit/fire-insurance-company/edit-fire-insurance-company.component';
import { FireInsuranceSideFormComponent } from './private/sale/property/fire-insurance/side-form/fire-insurance-side-form.component';
import { PropertyComponent } from './private/sale/property/property.component';
import { SaleComponent } from './private/sale/sale.component';
import { SideNavComponent } from './private/sale/side-nav/side-nav.component';
import { BalanceComponent } from './private/sale/teranet/balance/balance.component';
import { TeranetChangePasswordComponent } from './private/sale/teranet/change-password/change-password.component';
import { CheckSyncComponent } from './private/sale/teranet/check-sync/check-sync.component';
import { TeranetCompareNamesComponent } from './private/sale/teranet/compare-names/compare-names.component';
import { CompareNamesModalComponent } from './private/sale/teranet/compare-names/modal/modal.component';
import { ParcelCompareComponent } from './private/sale/teranet/compare-names/parcel/parcel.component';
import { TeranetDefaultComponent } from './private/sale/teranet/default/default.component';
import { TeranetDocketCreateComponent } from './private/sale/teranet/docket-create/docket-create.component';
import { TeranetInstrumentsComponent } from './private/sale/teranet/instruments/instruments.component';
import { PurchaseInstrumentComponent } from './private/sale/teranet/instruments/purchase-instrument/purchase-instrument.component';
import { TeranetCreateEncumbranceComponent } from './private/sale/teranet/instruments/teranet-create-encumbrance/teranet-create-encumbrance.component';
import { TeranetLoginComponent } from './private/sale/teranet/login/login.component';
import { TeranetMapLotsComponent } from './private/sale/teranet/map/lots/lots.component';
import { MapDialogComponent } from './private/sale/teranet/map/map-dialog/map-dialog.component';
import { AdjacentPropertiesMapComponent } from './private/sale/teranet/map/map.component';
import { TeranetNoPinComponent } from './private/sale/teranet/no-pin/no-pin.component';
import { TeranetOverviewComponent } from './private/sale/teranet/overview/overview.component';
import { ParcelComponent } from './private/sale/teranet/parcel/parcel.component';
import { PinViewComponent } from './private/sale/teranet/pin-view/pin-view.component';
import { TeranetPinStatusComponent } from './private/sale/teranet/status/status.component';
import { TeranetStepperAdjacentAnalysisComponent } from './private/sale/teranet/stepper/adjacent-analysis/adjacent-analysis.component';
import { TeranetStepperFooterComponent } from './private/sale/teranet/stepper/footer/footer.component';
import { TeranetStepperInstrumentsComponent } from './private/sale/teranet/stepper/instruments/instruments.component';
import { TeranetStepperRequisitionComponent } from './private/sale/teranet/stepper/requisition/requisition.component';
import { TeranetStepperSearchComponent } from './private/sale/teranet/stepper/search/search.component';
import { StepperComponent } from './private/sale/teranet/stepper/stepper.component';
import { TeranetStepperSyncComponent } from './private/sale/teranet/stepper/sync/sync.component';
import { TeranetStepperWritsComponent } from './private/sale/teranet/stepper/writs/writs.component';
import { ChargeTableComponent } from './private/sale/teranet/summary-docket/charge-table/charge-table.component';
import { DocketSyncComponent } from './private/sale/teranet/summary-docket/docket-sync/docket-sync.component';
import { TeranetSummaryModalComponent } from './private/sale/teranet/summary-docket/modal/modal.component';
import { SummaryDocketComponent } from './private/sale/teranet/summary-docket/summary-docket.component';
import { TeranetSyncClientSelectComponent } from './private/sale/teranet/sync/client-select/client-select.component';
import { ParcelsSyncFormComponent } from './private/sale/teranet/sync/sync-inner-form/parcels-sync-form/parcels-sync-form.component';
import { SyncInnerFormComponent } from './private/sale/teranet/sync/sync-inner-form/sync-inner-form.component';
import { TitleSyncFormComponent } from './private/sale/teranet/sync/sync-inner-form/title-sync-form/title-sync-form.component';
import { TitleSyncPropertyTypeWarningModalComponent } from './private/sale/teranet/sync/sync-inner-form/title-sync-form/title-sync-property-type-warning-modal/title-sync-property-type-warning-modal.component';
import { TeranetSyncDetailsComponent } from './private/sale/teranet/sync/sync.component';
import { TeranetGuardModalComponent } from './private/sale/teranet/teranet-guard-modal/modal.component';
import { TeranetComponent } from './private/sale/teranet/teranet.component';
import { OwnersTableComponent } from './private/sale/teranet/title-search/owners-table/owners-table.component';
import { PinsTableComponent } from './private/sale/teranet/title-search/pins-table/pins-table.component';
import { SearchByAddressComponent } from './private/sale/teranet/title-search/search-by-address/search-by-address.component';
import { SearchByInstrumentComponent } from './private/sale/teranet/title-search/search-by-instrument/search-by-instrument.component';
import { SearchByOwnerComponent } from './private/sale/teranet/title-search/search-by-owner/search-by-owner.component';
import { SearchByPinComponent } from './private/sale/teranet/title-search/search-by-pin/search-by-pin.component';
import { TitleSearchComponent } from './private/sale/teranet/title-search/title-search.component';
import { WritModule } from './private/sale/teranet/writ/writ.module';
import { ChicagoPortalComponent } from './private/sale/title-insurance/chicago/chicago-portal/chicago-portal.component';
import { ChicagoComponent } from './private/sale/title-insurance/chicago/chicago.component';
import { CancelModalComponent } from './private/sale/title-insurance/chicago/components/cancel-modal/cancel-modal.component';
import { ChicagoCancelledComponent } from './private/sale/title-insurance/chicago/components/cancelled/cancelled.component';
import { ChicagoFooterComponent } from './private/sale/title-insurance/chicago/components/footer/footer.component';
import { ChicagoNotSubmittedComponent } from './private/sale/title-insurance/chicago/components/not-submitted/not-submitted.component';
import { ChicagoReadyForPickupComponent } from './private/sale/title-insurance/chicago/components/ready-for-pickup/ready-for-pickup.component';
import { ChicagoUnderReviewComponent } from './private/sale/title-insurance/chicago/components/under-review/under-review.component';
import { AddTitleInsuranceComponent } from './private/sale/title-insurance/components/add-title-insurance/add-title-insurance.component';
import { TitleInsuranceContactInfoModalComponent } from './private/sale/title-insurance/components/contact-info-modal/contact-info-modal.component';
import { FinanceSettingsComponent } from './private/sale/title-insurance/components/finance-settings/finance-settings.component';
import { CounselCreditComponent } from './private/sale/title-insurance/components/input-fields/counsel-credit/counsel-credit.component';
import { EffectiveDateComponent } from './private/sale/title-insurance/components/input-fields/effective-date/effective-date.component';
import { OrderDateComponent } from './private/sale/title-insurance/components/input-fields/order-date/order-date.component';
import { OrderNumberComponent } from './private/sale/title-insurance/components/input-fields/order-number/order-number.component';
import { PremiumAmountComponent } from './private/sale/title-insurance/components/input-fields/premium-amount/premium-amount.component';
import { MissingFieldsNotifComponent } from './private/sale/title-insurance/components/missing-fields-notif/missing-fields-notif.component';
import { MmsMortgageWarningComponent } from './private/sale/title-insurance/components/mms-mortgage-warning/mms-mortgage-warning.component';
import { TitleInsuranceDocumentsComponent } from './private/sale/title-insurance/components/title-insurance-documents/title-insurance-documents.component';
import { TitleInsurancePoliciesComponent } from './private/sale/title-insurance/components/title-insurance-policies/title-insurance-policies.component';
import { TitleInsurancePriceOverrideModalComponent } from './private/sale/title-insurance/components/title-insurance-price-override-modal/title-insurance-price-override-modal.component';
import { TitleInsurancePriceOverrideComponent } from './private/sale/title-insurance/components/title-insurance-price-override/title-insurance-price-override.component';
import { TitleInsuranceToolbarComponent } from './private/sale/title-insurance/components/title-insurance-toolbar/title-insurance-toolbar.component';
import { CommercialFctComponent } from './private/sale/title-insurance/fct/commercial-fct/commercial-fct.component';
import { CommercialOrderSummaryComponent } from './private/sale/title-insurance/fct/components/commercial-order-summary/commercial-order-summary.component';
import { FCTCompletedComponent } from './private/sale/title-insurance/fct/components/completed/completed.component';
import { CostBreakdownComponent } from './private/sale/title-insurance/fct/components/cost-breakdown/cost-breakdown.component';
import { EnterPolicyDetailsComponent } from './private/sale/title-insurance/fct/components/enter-policy-details/enter-policy-details.component';
import { FctCancelModalComponent } from './private/sale/title-insurance/fct/components/fct-cancel-modal/fct-cancel-modal.component';
import { FctFooterComponent } from './private/sale/title-insurance/fct/components/fct-footer/fct-footer.component';
import { FctPortalComponent } from './private/sale/title-insurance/fct/components/fct-portal/fct-portal.component';
import { FormEntryComponent } from './private/sale/title-insurance/fct/components/form-entry/form-entry.component';
import { FCTInProgressComponent } from './private/sale/title-insurance/fct/components/in-progress/in-progress.component';
import { FctNotSubmittedComponent } from './private/sale/title-insurance/fct/components/not-submitted/not-submitted.component';
import { SubmitCommercialComponent } from './private/sale/title-insurance/fct/components/submit-commercial/submit-commercial.component';
import { FctTitleInsuranceComponent } from './private/sale/title-insurance/fct/fct.component';
import { ResidentialFctComponent } from './private/sale/title-insurance/fct/residential-fct/residential-fct.component';
import { AddPolicyModalComponent } from './private/sale/title-insurance/manual/components/add-policy-modal/add-policy-modal.component';
import { ClearOrderModalComponent } from './private/sale/title-insurance/manual/components/clear-order-modal/clear-order-modal.component';
import { DeletePolicyModalComponent } from './private/sale/title-insurance/manual/components/delete-policy-modal/delete-policy-modal.component';
import { HeaderComponent } from './private/sale/title-insurance/manual/components/header/header.component';
import { SwitchToIntegratedModalComponent } from './private/sale/title-insurance/manual/components/switch-to-integrated-modal/switch-to-integrated-modal.component';
import { ManualTitleInsuranceComponent } from './private/sale/title-insurance/manual/manual.component';
import { ManualTitleInsuranceUploadFileComponent } from './private/sale/title-insurance/manual/upload-file-modal/upload-file-modal.component';
import { StewartCancelModalComponent } from './private/sale/title-insurance/stewart/components/cancel-modal/cancel-modal.component';
import { StewartCancelledComponent } from './private/sale/title-insurance/stewart/components/cancelled/cancelled.component';
import { StewartDeclinedComponent } from './private/sale/title-insurance/stewart/components/declined/declined.component';
import { StewartFlaggedComponent } from './private/sale/title-insurance/stewart/components/flagged/flagged.component';
import { StewartFooterComponent } from './private/sale/title-insurance/stewart/components/footer/footer.component';
import { MessageContainerComponent } from './private/sale/title-insurance/stewart/components/message-container/message-container.component';
import { StewartNotSubmittedComponent } from './private/sale/title-insurance/stewart/components/not-submitted/not-submitted.component';
import { StewartOrderedComponent } from './private/sale/title-insurance/stewart/components/ordered/ordered.component';
import { ProfileIdMessageComponent } from './private/sale/title-insurance/stewart/components/profile-id-message/profile-id-message.component';
import { StewartModalComponent } from './private/sale/title-insurance/stewart/stewart-modal/stewart-modal.component';
import { StewartComponent } from './private/sale/title-insurance/stewart/stewart.component';
import { TitleInsuranceComponent } from './private/sale/title-insurance/title-insurance.component';
import { TitlePLUSFooterComponent } from './private/sale/title-insurance/title-plus/components/footer/title-plus-footer.component';
import { TitlePlusCancelModalComponent } from './private/sale/title-insurance/title-plus/components/title-plus-cancel-modal/title-plus-cancel-modal.component';
import { TitlePlusCancelledComponent } from './private/sale/title-insurance/title-plus/components/title-plus-cancelled/title-plus-cancelled.component';
import { TitlePlusDeclinedComponent } from './private/sale/title-insurance/title-plus/components/title-plus-declined/title-plus-declined.component';
import { TitlePlusIssuedComponent } from './private/sale/title-insurance/title-plus/components/title-plus-issued/title-plus-issued.component';
import { TitlePlusModalComponent } from './private/sale/title-insurance/title-plus/components/title-plus-modal/title-plus-modal.component';
import { TitlePlusNotSubmittedComponent } from './private/sale/title-insurance/title-plus/components/title-plus-not-submitted/title-plus-not-submitted.component';
import { TitlePlusUnderReviewComponent } from './private/sale/title-insurance/title-plus/components/title-plus-under-review/title-plus-under-review.component';
import { TitlePlusComponent } from './private/sale/title-insurance/title-plus/title-plus.component';
import { ChecklistNavBarComponent } from './private/sale/top-nav-bar/checklist-nav-bar/checklist-nav-bar.component';
import { DocumentDropDownComponent } from './private/sale/top-nav-bar/document-drop-down/document-drop-down.component';
import { DocumentsNavBarComponent } from './private/sale/top-nav-bar/documents-nav-bar/documents-nav-bar.component';
import { MatterSearchComponent } from './private/sale/top-nav-bar/matter-search/matter-search.component';
import { TopNavBarComponent } from './private/sale/top-nav-bar/top-nav-bar.component';
import { StewartCallbackComponent } from './private/stewart-callback/stewart-callback.component';
import { TitlePlusCallbackComponent } from './private/title-plus-callback/title-plus-callback.component';
import { AuthContainerComponent } from './public/auth/auth-container/auth-container.component';
import { AuthComponent } from './public/auth/auth.component';
import { LeapLoginComponent } from './public/auth/leap-login/login.component';
import { ForgotComponent } from './public/auth/login/forgot/forgot.component';
import { ResetComponent } from './public/auth/login/forgot/reset/reset.component';
import { LoginComponent } from './public/auth/login/login.component';
import { OnboardingContainerComponent } from './public/auth/onboarding-container/onboarding-container.component';
import { PendingInviteComponent } from './public/auth/pending-invite/pending-invite.component';
import { CreateUserBusinessComponent } from './public/auth/register/create-user/create-user-business/create-user-business.component';
import { CreateUserInviteComponent } from './public/auth/register/create-user/create-user-invite/create-user-invite.component';
import { CreateUserProfileComponent } from './public/auth/register/create-user/create-user-profile/create-user-profile.component';
import { CreateUserComponent } from './public/auth/register/create-user/create-user.component';
import { RegisterComponent } from './public/auth/register/register.component';
import { SignUpComponent } from './public/auth/sign-up/sign-up.component';
import { VerifyEmailComponent } from './public/auth/verify-email/verify-email.component';
import { ContactComponent } from './public/contact/contact.component';
import { FourohfourComponent } from './public/invalid/fourohfour/fourohfour.component';
import { FourohthreeComponent } from './public/invalid/fourohthree/fourohthree.component';
import { LandingComponent } from './public/landing/landing.component';
import { LeapState } from './public/leap/state/leap.state';
import { LegalComponent } from './public/legal/legal.component';
import { ProxyTestComponent } from './public/proxy-test/proxy-test.component';
import { AtomicModule } from './shared/atomic/atomic.module';
import { ExactLengthDirective } from './shared/core/directives/validators/exactLength.directive';
import { MaxLengthDirective } from './shared/core/directives/validators/max-length.directive';
import { MinLengthDirective } from './shared/core/directives/validators/min-length.directive';
import { PinFormattingDirective } from './shared/core/directives/validators/pin-formatting.directive';
import { XMLSpecialCharactersDirective } from './shared/core/directives/validators/xml-special-characters.directive';
import { HtmlTemplatingModule } from './shared/modules/html-templating/html-templating.module';
import { StripeModule } from './shared/modules/stripe/stripe.module';
import { UrlHistoryService } from './shared/services/urlHistory/urlHistory.service';
import { UiModule } from './ui/ui.module';
import {
	PROVIDE_CLIENT_INTAKE_RESPONSES_STATE,
	PROVIDE_CURRENT_CLIENT_INTAKE_RESPONSE_STATE,
} from '@db-access/clientIntakeResponses';
import { PROVIDE_INTAKE_FORM_ITEMS_STATE } from '@domain/intake-form-items';
import { PROVIDE_INTAKE_FORM_CONTEXT_STATE } from '@domain/intake-form-context';
import { PROVIDE_PORTAL_ENGINE_STATE } from '@domain/portal-engine';
import { PROVIDE_CLIENT_INTAKE_FORM_ITEMS_STATE } from '@db-access/clientIntakeFormItems';
import { PROVIDE_CLIENT_INTAKE_FORM_CONTEXT_STATE } from '@db-access/clientIntakeFormContext';
import { PortalInviteState } from './shared/state/portal-invite/portal-invite.state';
import { PortalUsersState } from 'libs/db-access/portalUsers/src/state/portalUsers.firebase.state';
import { MatterMortgageFormTemplatesCurrentVersionState } from '@state/matter/matter_mortgages/matter-mortgage-forms/matter-mortgage-form-templates-current-version/state';
import { MatterEnvelopesState } from './shared/state/matter/matter-envelopes/matter-envelopes.state';

import { EmailIntegrationState } from '@state/email-integration/state';
import { OutlookCallbackComponent } from './private/email-integration/outlook-callback/outlook-callback.component';
import { themeConfig } from '@core/ui';
import { TenantsFormComponent } from './private/sale/fields/property-type/tenants-form/tenants-form.component';
import { TenantsListComponent } from './private/sale/fields/property-type/tenants-list/tenants-list.component';
import { TenantsModalComponent } from './private/sale/fields/property-type/tenants-modal/tenants-modal.component';
import { RentalEquipmentComponent } from './private/sale/fields/property-type/rental-equipment/rental-equipment.component';
import { EmployerDetailsComponent } from './private/global-profile/details/person/employer-details/employer-details.component';
import { GhostPracticeImportModalComponent } from './private/manage/statement-of-account/defaults/ghost-practice-import-modal/ghost-practice-import-modal.component';
import { ClientIntakeCardHeaderComponent } from './private/sale/parties/components/client-intake-actions/client-intake-card-header.component';

// https://docs.logrocket.com/reference/init
LogRocket.init(environment.logRocketProject, {
	release: environment.logRocketRelease,
});
LogRocket.getSessionURL((x) =>
	console.log('LogRocket has been initialized. The session id is: ', x),
);

@NgModule({
	declarations: [
		AppComponent,
		LandingComponent,
		OutlookCallbackComponent,
		ManageComponent,
		AuthComponent,
		LoginComponent,
		LeapLoginComponent,
		ForgotComponent,
		ResetComponent,
		RegisterComponent,
		LegalComponent,
		ApsComponent,
		HistoryComponent,
		CurrentUsersComponent,
		OutlookCallbackComponent,
		SaleComponent,
		SaleHomeComponent,
		AccountsComponent,
		StatementOfAccountTableComponent,
		AddMatterStatementOfAccountComponent,
		MatterStatementOfAccountsEditDocumentTextComponent,
		AppMainComponent,
		InvitesComponent,
		IdvRequestModalComponent,
		BillingComponent,
		BusinessDetailsComponent,
		AddressDialogComponent,
		EditingDefaultsDialogComponent,
		SalePriceComponent,
		DepositComponent,
		MatterAddressComponent,
		InterestEstateComponent,
		AgreementDateComponent,
		CreateMiniClientComponent,
		ClosingDateComponent,
		TitleSearchCompletionDateComponent,
		EditClientComponent,
		CreateClientMiniModalComponent,
		EditClientModalComponent,
		SaleLocationComponent,
		AdjustmentsComponent,
		DebtsComponent,
		DebtDialogComponent,
		NewDocumentDialogComponent,
		LetterheadEditorDialogComponent,
		IdProofUploadDialogComponent,
		MapDialogComponent,
		MergeDocumentDialogComponent,
		CommonExpensesDialogComponent,
		DepositDialogComponent,
		HeatDialogComponent,
		OccupancyFeesDialogComponent,
		PropertyTaxDialogComponent,
		SalePriceDialogComponent,
		TarrionWarrantyDialogComponent,
		TenancyCurrentDialogComponent,
		TenancyPrepaidDialogComponent,
		TemplateSidebarComponent,
		LedgerComponent,
		PropertyComponent,
		SideNavComponent,
		CreateLawFirmComponent,
		ProxyTestComponent,
		LawFirmsComponent,
		CreateLawFirmEmployeeComponent,
		LawFirmSearchComponent,
		LawyerComponent,
		ClerkComponent,
		BrokerageSearchComponent,
		CreateBrokerageComponent,
		BrokerageComponent,
		CreateBrokerComponent,
		DefaultsComponent,
		GeneralDefaultsComponent,
		StatementOfAccountGeneralComponent,
		StatementOfAccountDefaultsComponent,
		DocumentsTemplatesComponent,
		TeranetComponent,
		TeranetLoginComponent,
		ProfileComponent,
		ProfileDetailsComponent,
		ProfilePreferencesComponent,
		TeranetUsernameComponent,
		ProfileTeranetComponent,
		TitleSearchComponent,
		TeranetDefaultComponent,
		LroComponent,
		LROSuggestionComponent,
		SearchByOwnerComponent,
		OwnersTableComponent,
		PinsTableComponent,
		SearchByAddressComponent,
		SearchByPinComponent,
		SearchByInstrumentComponent,
		TeranetDocketCreateComponent,
		TeranetInstrumentsComponent,
		TeranetGuardModalComponent,
		AdjacentPropertiesMapComponent,
		ParcelComponent,
		TeranetMapLotsComponent,
		PinViewComponent,
		TeranetSyncDetailsComponent,
		TeranetCompareNamesComponent,
		ParcelCompareComponent,
		StepperComponent,
		TeranetStepperRequisitionComponent,
		TeranetOverviewComponent,
		BalanceComponent,
		CompareNamesModalComponent,
		TeranetSyncClientSelectComponent,
		TeranetStepperSearchComponent,
		TeranetStepperSyncComponent,
		TeranetStepperInstrumentsComponent,
		TeranetStepperAdjacentAnalysisComponent,
		TeranetStepperWritsComponent,
		TeranetStepperFooterComponent,
		TeranetNoPinComponent,
		SyncInnerFormComponent,
		TeranetPinStatusComponent,
		TeranetChangePasswordComponent,
		SummaryDocketComponent,
		TeranetSummaryModalComponent,
		ChargeTableComponent,
		DocketSyncComponent,
		EditBusinessClientComponent,
		EditPersonClientComponent,
		CreatePersonClientComponent,
		CreateBusinessClientComponent,
		BankingComponent,
		AssociationComponent,
		AssociationsComponent,
		ClientIdentificationComponent,
		ClientDetailsComponent,
		ClientBusinessDetailsComponent,
		PersonDetailsComponent,
		AddClientAssociationButtonComponent,
		PersonIdentificationComponent,
		BusinessIdentificationComponent,
		IdentificationFormComponent,
		CommissionComponent,
		SaleDocsComponent,
		DocumentComponent,
		DocumentBlockComponent,
		FolderDeleteDialogComponent,
		DocumentDeleteDialogComponent,
		FolderEditDialogComponent,
		FolderMoveDialogComponent,
		TemplateFolderDeleteDialogComponent,
		TemplateFolderEditDialogComponent,
		TemplateMoveDialogComponent,
		UploadedDocumentsComponent,
		ResponseToRequisitionsComponent,
		UndertakingsComponent,
		UndertakingDialogComponent,
		UploadDocumentModalComponent,
		PropertyDetailsComponent,
		PropertyCondoCorpComponent,
		PartiesComponent,
		PartiesClientsComponent,
		PartiesOtherPartiesComponent,
		BrokerComponent,
		PropertyTypeComponent,
		DepositHolderComponent,
		IntegrationsComponent,
		LlcIntegrationComponent,
		LLCLoginComponent,
		PortalIframeComponent,
		LLCExistingMatterModalComponent,
		LLCNewMatterModalComponent,
		PurchasePriceComponent,
		SyncDataComponent,
		MatterMortgagesComponent,
		LlcMatterMortgageComponent,
		LLCMortgageDetailsComponent,
		CloserSyncMortgageDetailsComponent,
		CloserSyncReportingComponent,
		CloserSyncLendersComponent,
		CloserSyncTermsComponent,
		CloserSyncDocumentsComponent,
		LLCLenderDetailsComponent,
		LLCReportComponent,
		LLCContactCardComponent,
		MatterMortgageComponent,
		FormsComponent,
		LlcStepperComponent,
		ComplianceLettersComponent,
		FireInsuranceComponent,
		TitleInsuranceComponent,
		LLCAmendmentsModalComponent,
		TdAcknowledgeModalComponent,
		SyncAmendmentUsersComponent,
		BaseIdentificationFormComponent,
		PartyClientsHeaderComponent,
		PartyCapacityComponent,
		DownloadDocumentsComponent,
		LlcFileDownloadStatusComponent,
		LlcDownloadsTableComponent,
		PropertyUnitsModalComponent,
		EncumbrancesComponent,
		EncumbranceComponent,
		CreateEncumbranceComponent,
		MortgageDetailsComponent,
		EncumbranceMortgageeComponent,
		EncumbranceInstitutionComponent,
		EncumbrancePrivateLenderComponent,
		AddStatementOfAccountDefaultComponent,
		StatementOfAccountDefaultListComponent,
		TopNavBarComponent,
		RightSideComponent,
		CreateNoteComponent,
		MatterSearchComponent,
		SolicitorComponent,
		ContactsComponent,
		ContactComponent,
		CreateInstitutionBranchComponent,
		OtherDialogComponent,
		LedgerInstructionsModalComponent,
		CustomStatementComponent,
		ImportDetailsComponent,
		KeyDatesModalComponent,
		EditDocumentTextComponent,
		KeysModalComponent,
		EditMatterDetailsComponent,
		CreateInstitutionComponent,
		ContactInfoFormComponent,
		TeranetCreateEncumbranceComponent,
		FctTitleInsuranceComponent,
		ManualTitleInsuranceComponent,
		ManualTitleInsuranceUploadFileComponent,
		CreateClientSideFormComponent,
		CreatePersonClientSideFormComponent,
		CreateBusinessClientSideFormComponent,
		ApsSideFormComponent,
		EditClientSideFormComponent,
		EditPersonClientSideFormComponent,
		EditBusinessClientSideFormComponent,
		ClientsSearchAndCreateComponent,
		AddTitleInsuranceComponent,
		ChicagoComponent,
		ChicagoNotSubmittedComponent,
		ChicagoUnderReviewComponent,
		ChicagoReadyForPickupComponent,
		ChicagoCancelledComponent,
		ChicagoFooterComponent,
		ChicagoProfileComponent,
		ConfirmFirmModalComponent,
		FctNotSubmittedComponent,
		FctPortalComponent,
		FCTInProgressComponent,
		FCTCompletedComponent,
		BrokerageSideFormComponent,
		BrokerageSearchSideFormInputComponent,
		CreateBrokerageSideFormComponent,
		EditBrokerageSideFormComponent,
		BrokerSideFormComponent,
		EditBrokerSideFormComponent,
		LawFirmsSideFormComponent,
		LawFirmSearchSideFormComponent,
		CreateLawFirmSideFormComponent,
		LawyerSideFormComponent,
		EditLawFirmSideFormComponent,
		EditLawyerSideFormComponent,
		FourohfourComponent,
		FctIntegrationComponent,
		PaymentCalculatorSideFormComponent,
		EncumbranceSideFormComponent,
		SaleDepositSideFormComponent,
		FileSalutationsComponent,
		EditingSalutationsReLinesDialogComponent,
		AddMortgageComponent,
		ManualMortgageComponent,
		CloserSyncMortgageComponent,
		DeleteDocumentModalComponent,
		EditCategoryModalComponent,
		ManualMortgageLenderDetailsComponent,
		EditingMortgageReLineDialogComponent,
		ManualMortgageTermsComponent,
		ManualMortgageDetailsComponent,
		CostBreakdownComponent,
		FinanceSettingsComponent,
		SearchFIreInsuranceCompanyComponent,
		SearchFireInsuranceAgentComponent,
		SearchFireInsuranceBrokerageComponent,
		SearchFireInsuranceBrokerComponent,
		CreateFireInsuranceCompanyComponent,
		CreateFireInsuranceBrokerageComponent,
		FireInsuranceSideFormComponent,
		EditFireInsuranceBrokerageComponent,
		EditFireInsuranceBrokerComponent,
		EditFireInsuranceCompanyComponent,
		EditFireInsuranceAgentComponent,
		PinFormattingDirective,
		AtLeastOneRequiredDirective,
		XMLSpecialCharactersDirective,
		MaxLengthDirective,
		MinLengthDirective,
		UndertakingItemComponent,
		SearchManagementCompanyComponent,
		SearchCondoCorporationComponent,
		CondoCorporationModalComponent,
		DeleteConfirmationModalComponent,
		BusinessFinancesGeneralComponent,
		BusinessFinancesGeneralComponent,
		ExecutionAffidavitsComponent,
		LttComponent,
		CondoCorporationAttentionContactModalComponent,
		GuarantorsComponent,
		ClientTabComponent,
		MatterMortgagePaymentsComponent,
		SearchMortgageBrokerageComponent,
		SearchMortgageBrokerComponent,
		MortgageBrokerageModalComponent,
		MortgageBrokerModalComponent,
		SetFeeComponent,
		NetAmountComponent,
		MortgageBrokerComponent,
		AddLenderFormModalComponent,
		LenderFormsMortgageCardComponent,
		LoanAmountComponent,
		PurchaseRequisitionsComponent,
		UpdateRequisitionModalComponent,
		PartiesMortgagorsComponent,
		LoanAmountComponent,
		PaymentReceiptModalComponent,
		EditGeneratedTitleComponent,
		ResourcesComponent,
		TemplateVersioningComponent,
		MatterTypeSelectorComponent,
		FooterSelectorComponent,
		HeaderSelectorComponent,
		PendingChangesDialogComponent,
		FormViewerComponent,
		ViewTeranetAccountComponent,
		CheckSyncComponent,
		IdleTimeoutWarningComponent,
		SecurityComponent,
		ChangePriorityComponent,
		ChargeTypeDropdownComponent,
		AdjustmentDefaultsComponent,
		FormViewerComponent,
		PurchaseInstrumentComponent,
		TrustLedgerDefaultsComponent,
		ChicagoPortalComponent,
		CancelModalComponent,
		ComputershareBranchSelectComponent,
		StewartCallbackComponent,
		OutlookCallbackComponent,
		StewartComponent,
		StewartProfileComponent,
		StewartModalComponent,
		StewartNotSubmittedComponent,
		StewartFlaggedComponent,
		StewartCancelledComponent,
		StewartDeclinedComponent,
		StewartOrderedComponent,
		StewartCancelModalComponent,
		StewartFooterComponent,
		HstSalePriceDialogComponent,
		HstRebateDialogComponent,
		SalePriceBreakdownComponent,
		AssociationModalComponent,
		TitleInsuranceGeneralComponent,
		DirectionOfAmountComponent,
		MessageContainerComponent,
		ClearOrderModalComponent,
		DocumentStylingComponent,
		FctFooterComponent,
		OccupancyDateComponent,
		TitleInsuranceContactInfoModalComponent,
		TitleInsuranceToolbarComponent,
		DocumentStylingComponent,
		OccupancyDateComponent,
		NoteComponent,
		DocumentsNavBarComponent,
		FctCancelModalComponent,
		MissingFieldsNotifComponent,
		FctCancelModalComponent,
		AddPolicyModalComponent,
		DeletePolicyModalComponent,
		SwitchToIntegratedModalComponent,
		MatterActivityComponent,
		LastOpenedTabsComponent,
		DocumentDropDownComponent,
		DocumentDropDownComponent,
		CreateUserComponent,
		CreateUserProfileComponent,
		CreateUserBusinessComponent,
		CreateUserInviteComponent,
		InviteModalComponent,
		ProfileIdMessageComponent,
		UploadedDeleteModalComponent,
		FourohthreeComponent,
		AuthContainerComponent,
		DeleteJurisdictionConfirmationDialogComponent,
		VacantPropertyDialogComponent,
		JurisdictionFieldComponent,
		HCRADialogComponent,
		UploadedFileEditModalComponent,
		ChicagoFirmNameOverrideComponent,
		MortgageIntakeComponent,
		MortgageIntakeFieldsComponent,
		PriorityWarningComponent,
		ChargeTypeSelectComponent,
		IntakeCancelModalComponent,
		NoFormsAvailableComponent,
		AssystFeeComponent,
		NameFormattingComponent,
		PropertyUnitOverviewComponent,
		StewartProfileIdsComponent,
		TitleSyncFormComponent,
		ParcelsSyncFormComponent,
		ParcelOfTiedLandFormComponent,
		ParcelOfTiedLandOverviewComponent,
		TitleSyncPropertyTypeWarningModalComponent,
		PropertyTypeSelectionComponent,
		TitleInsuranceDocumentsComponent,
		TitleInsurancePoliciesComponent,
		CounselCreditComponent,
		PremiumAmountComponent,
		EffectiveDateComponent,
		OrderDateComponent,
		OrderNumberComponent,
		HeaderComponent,
		CommercialFctComponent,
		ResidentialFctComponent,
		FormEntryComponent,
		SubmitCommercialComponent,
		EnterPolicyDetailsComponent,
		CommercialOrderSummaryComponent,
		TreefortProfileComponent,
		ProfileBenefitsComponent,
		BackToIntegrationsButtonComponent,
		TreefortOnboardingModalComponent,
		IdvFinancesGeneralComponent,
		IdvGeneralComponent,
		CorrespondenceCardComponent,
		IdvTableComponent,
		SendRequestsComponent,
		ClientsSelectComponent,
		CancelRequestsComponent,
		LenderPercentageOfInterestComponent,
		BusinessChecklistComponent,
		AddTemplateDialogComponent,
		ChecklistContainerComponent,
		MatterChecklistComponent,
		MatterChecklistEmptyStateComponent,
		ChecklistNavBarComponent,
		MortgageHeaderComponent,
		MatterChecklistStageComponent,
		IdvReviewModalComponent,
		ReviewReportComponent,
		SyncIdDataComponent,
		IdDetailsComponent,
		IdvTreefortVerificationGeneralComponent,
		ConfirmDetailsComponent,
		TitlePlusProfileComponent,
		TitlePlusLsnComponent,
		TitlePlusComponent,
		TitlePlusNotSubmittedComponent,
		TitlePLUSFooterComponent,
		CosmolexComponent,
		AccountingComponent,
		CosmolexComponent,
		CosmolexAccountingItemsTableComponent,
		ValueAndLtvComponent,
		LttSettingsComponent,
		LTTSettingsDialogComponent,
		CloserSyncComponent,
		TemplateSidebarComponent,
		ContextDialogComponent,
		ManageTemplatesContactAdminsComponent,
		TrustAccountsComponent,
		ConnectMainModalComponent,
		CloserSyncSettingsDrawerComponent,
		DepositHolderDropdownComponent,
		MultipleCommissionsComponent,
		SolunoImportModalComponent,
		GhostPracticeImportModalComponent,
		LeapManageModalComponent,
		LeapExportModalComponent,
		SolunoAccountingItemsTableComponent,
		GhostPracticeAccountingItemsTableComponent,
		LeapAccountingItemsTableComponent,
		SolunoComponent,
		GhostPracticeComponent,
		LeapAccountingComponent,
		SignaturePadComponent,
		TemplateSidebarComponent,
		ContextDialogComponent,
		ManageTemplatesContactAdminsComponent,
		TitlePlusModalComponent,
		TitlePlusUnderReviewComponent,
		TitlePlusIssuedComponent,
		TitlePlusCancelledComponent,
		TitlePlusDeclinedComponent,
		TitlePlusCancelModalComponent,
		SignaturePadComponent,
		TitlePlusCallbackComponent,
		CloserSyncSignatureRequestModalComponent,
		RequiredPinModalComponent,
		CloserSyncInstitutionSaveModalComponent,
		WelcomeModalComponent,
		TermsOfServiceComponent,
		CloserSyncGeneralSettingsComponent,
		CloserSyncLenderDocsComponent,
		CloserSyncEarlyAccessComponent,
		CloserSyncCongratsConnectedComponent,
		CloserSyncGeneralDetailsComponent,
		CloserSyncPromptsComponent,
		DisconnectAssystComponent,
		CommissionDetailsComponent,
		CommissionStatementFileHandlerComponent,
		DepositDrawerComponent,
		DepositSideFormComponent,
		CommissionBrokerageSelectionComponent,
		VtbMortgageDeleteModalComponent,
		VtbMortgageDialogComponent,
		RequisitionsTemplatesComponent,
		RequiredListComponent,
		ExactLengthDirective,
		MaliciousUserPageComponent,
		ClientTabProfileComponent,
		VerifyEmailComponent,
		PendingInviteComponent,
		OnboardingContainerComponent,
		TreefortPromotionBannerComponent,
		DocumentFormattingComponent,
		GlobalProfileComponent,
		LeapCreateMatterComponent,
		MortgageDrawerComponent,
		SalePriceItemDrawerComponent,
		SalePriceItemizationComponent,
		SignUpComponent,
		ChargeComponent,
		ChargesComponent,
		TransferComponent,
		ViewInviteDialogComponent,
		EregFileViewerComponent,
		LeapComponent,
		ReviewXmlFieldsComponent,
		ReviewTransferFieldsComponent,
		EditDocumentDrawerComponent,
		DocxViewerDialogComponent,
		MmsMortgageWarningComponent,
		ClientMergeReviewComponent,
		AiScrapingNotificationComponent,
		ReviewSimilarPartyComponent,
		AiDocumentProcessingLoadingComponent,
		BusinessBaseDocxTableComponent,
		BusinessBaseDocxDrawerComponent,
		AiDocumentProcessingDisclaimerComponent,
		LoadingModalComponent,
		DeactivateUserModalComponent,
		IntegrationCardComponent,
		TitleSearchSettingsComponent,
		InstrumentRegistrationSettingsComponent,
		SignItComponent,
		SignItOnboardingModalComponent,
		DirectionOfFeeDropdownComponent,
		ESigningGeneralComponent,
		CondoComponent,
		CondoCorpSearchCardComponent,
		CondoMgmtSearchCardComponent,
		CondoManagementTypeComponent,
		AddressFormattingComponent,
		TitleInsurancePriceOverrideComponent,
		TitleInsurancePriceOverrideModalComponent,
		TenantsFormComponent,
		TenantsListComponent,
		TenantsModalComponent,
		RentalEquipmentComponent,
		EmployerDetailsComponent,
	],
	imports: [
		LeapModule,
		EditorModule, // still used by private/manage/documents/templates/letter-editor-dialog
		HtmlTemplatingModule,
		CommonModule,
		MatNativeDateModule,
		DragDropModule,
		NavbarComponent,
		ComponentsModule,
		NgClickOutsideDirective,
		NoopAnimationsModule,
		BrowserAnimationsModule,
		ObserversModule,
		AppRoutingModule,
		AngularFireModule.initializeApp(environment.firebase),
		AngularFireAnalyticsModule,
		AngularFireFunctionsModule,
		AngularFireAuthModule,
		AngularFireStorageModule,
		AngularFireDatabaseModule,
		FormsModule,
		IconsModule,
		MatMenuModule,
		MatButtonModule,
		MatInputModule,
		MatFormFieldModule,
		MatTableModule,
		MatDatepickerModule,
		ReactiveFormsModule,
		MatSnackBarModule,
		MatSortModule,
		MatAutocompleteModule,
		MatSlideToggleModule,
		MatCardModule,
		MatExpansionModule,
		MatProgressSpinnerModule,
		MatProgressBarModule,
		MatButtonToggleModule,
		MatDividerModule,
		MatSidenavModule,
		MatListModule,
		MatToolbarModule,
		MatTooltipModule,
		MatSelectModule,
		MatRippleModule,
		MatChipsModule,
		MatIconModule,
		MatDialogModule,
		MarkdownModule.forRoot(),
		MatStepperModule,
		CoreModule,
		NgxPrintModule,
		AtomicModule,
		HighlightModule,
		IntakeDropdownComponent,
		ClientIntakeRequestComponent,
		ClientIntakeCardHeaderComponent,
		GlodalDataManagementModule,
		RequisitionsModule,
		CreateMatterModule,
		NgCircleProgressModule.forRoot(),
		MortgagesModule,
		ScrollingModule,
		WritModule,
		NgxsModule.forRoot(
			[
				MatterNGXSState,
				AuthState,
				BusinessUsersState,
				ClientsState,
				BusinessState,
				ClientSearchState,
				MatterClientsState,
				GoogleSearchState,
				LawFirmSearchState,
				LawyerSearchState,
				EmailIntegrationState,
				BrokerageSearchState,
				FinancialInstitutionBranchSearchState,
				BrokerSearchState,
				UserProfileState,
				SalePinState,
				SaleWritState,
				SaleInstrumentsState,
				FileUploadState,
				MatterSearchState,
				MatterMortgagesState,
				MatterMortgagePackagesState,
				LLCMortgageState,
				MatterChecklistsState,
				BusinessChecklistsState,
				GlobalEmailTemplatesState,
				BusinessEmailTemplatesState,
				DebtsState,
				AffidavitState,
				DocumentsState,
				MatterPackagesState,
				MatterEnvelopesState,
				BusinessDocumentsState,
				BusinessAddressesState,
				MatterUploadedFilesState,
				MatterEncumbrancesState,
				MatterStatementOfAccountsState,
				MatterNotesState,
				UndertakingsState,
				AdjustmentsState,
				StatementOfAccountDefaultsState,
				GlobalStatementOfAccountDefaultsState,
				MatterFormDataState,
				MatterFormV2DataState,
				FinancialInstitutionSearchState,
				RecentlyViewedState,
				TagsState,
				DashboardState,
				CondoCorporationsSearchState,
				CondoCorporationManagementsSearchState,
				FireInsuranceBrokeragesSearchState,
				FireInsuranceCompaniesSearchState,
				FireInsuranceBrokersSearchState,
				FireInsuranceAgentsSearchState,
				MortgageBrokeragesSearchState,
				MortgageBrokerSearchState,
				GlobalDataManagementState,
				MattersListOverviewState,
				MatterRequisitionsState,
				IdleTimeoutState,
				GlobalDocumentsState,
				JurisdictionSearchState,
				BusinessFoldersState,
				TrustAccountsState,
				MatterFoldersState,
				ReportingBusinessState,
				DocumentStylingState,
				SelectedMatterDocumentsState,
				UserSessionsState,
				NavigationState,
				JurisdictionsState,
				AppState,
				FileDownloadState,
				MatterTitlesState,
				LlcState,
				TitlePlusState,
				SelectedMatterFormsState,
				InstanceState,
				UserPreferencesState,
				UIBackdropState,
				MatterAccountingState,
				LocalBranchesState,
				BillingBusinessTypesState,
				BusinessRequisitionsState,
				GlobalSyncInstitutionsState,
				GlobalFinancialInstitutionsState,
				SyncMortgagesInstructionsState,
				SyncMortgagesPackagesState,
				FeatureFlagState,
				MatterMortgageFormTemplatesState,
				BusinessWordDocumentsState,
				GlobalWordDocumentsState,
				LeapState,
				BusinessBaseDocxsState,
				CloserTasksState,
				MatterPackagesHistoryState,
				MatterWordDocumentsState,
				MatterDocumentPackagesAggregatorState,
				MatterAllDocumentsAggregatorState,
				DocumentRequestsState,
				EmailTemplatesState,
				LoadingModalState,
				UserMattersUsageState,
				GenericFormsTemplatesState,
				RemoteConfigState,
				MatterClientIntakeFormState,
				PortalInviteState,
				PortalUsersState,
				MatterMortgageFormTemplatesCurrentVersionState,
			],
			{
				developmentMode: environment.env === envTypes.DEVELOPMENT,
				selectorOptions: { injectContainerState: false, suppressErrors: false },
			},
		),
		NgxsStoragePluginModule.forRoot({ keys: ['app.loadedFromLeap'] }),
		NgxsReduxDevtoolsPluginModule.forRoot({
			disabled: environment.env === envTypes.PRODUCTION,
		}),
		NgxsRouterPluginModule.forRoot(),
		NgxsDispatchPluginModule.forRoot(),
		NgxsSelectSnapshotModule.forRoot(),
		StripeModule,
		ServiceWorkerModule.register('ngsw-worker.js', {
			enabled:
				environment.env === envTypes.PRODUCTION ||
				environment.env === envTypes.STAGING,
			// Register the ServiceWorker as soon as the app is stable
			// or after 30 seconds (whichever comes first).
			registrationStrategy: 'registerWhenStable:30000',
		}),
		UiModule,
		GeneralSettingsComponent,
	],
	providers: [
		{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
		{
			provide: FIRESTORE_SETTINGS,
			useValue: { ignoreUndefinedProperties: true, merge: true },
		},
		{
			provide: USE_FIRESTORE_EMULATOR,
			useValue:
				environment.env === envTypes.DEVELOPMENT
					? [emulatorUrl, 8082]
					: undefined,
		},
		{
			provide: USE_FUNCTIONS_EMULATOR,
			useValue:
				environment.env === envTypes.DEVELOPMENT
					? [emulatorUrl, 5002]
					: undefined,
		},
		{
			provide: USE_DATABASE_EMULATOR,
			useValue:
				environment.env === envTypes.DEVELOPMENT
					? [emulatorUrl, 9000]
					: undefined,
		},
		{
			provide: USE_AUTH_EMULATOR,
			useValue:
				environment.env === envTypes.DEVELOPMENT
					? [`http://${emulatorUrl}:9099`]
					: undefined,
		},
		{
			provide: USE_STORAGE_EMULATOR,
			useValue:
				environment.env === envTypes.DEVELOPMENT
					? [emulatorUrl, 9199]
					: undefined,
		},
		{
			provide: REGION,
			useValue: environment.region,
		},
		{
			provide: HIGHLIGHT_OPTIONS,
			useValue: {
				coreLibraryLoader: () => import('highlight.js/lib/core'),
				languages: {
					xml: () => import('highlight.js/lib/languages/xml'),
				},
			},
		},
		{
			provide: MAT_DATE_FORMATS,
			useValue: {
				display: {
					dateInput: 'll',
					monthYearLabel: 'YYYY',
					dateA11yLabel: 'LL',
					monthYearA11yLabel: 'YYYY',
				},
			},
		},
		{ provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
		[CentsToDollarsPipe, CentsToDollarsFixedPipe], // note - this should be removed from here, as its declared in core.module - needs lots o testing or strict templates enabled
		[PercentPipe, TruncateStringPipe],
		PendingChangesGuard,
		CookieService,
		ClientSearchService,
		provideFirebaseApp(() => initializeApp(environment.firebase)),
		provideAuth(() => {
			const auth = getAuth();
			if (environment.env === envTypes.DEVELOPMENT) {
				connectAuthEmulator(auth, `http://${emulatorUrl}:9099`, {
					disableWarnings: true,
				});
			}
			return auth;
		}),
		provideStorage(() => {
			const storage = getStorage();
			if (environment.env === envTypes.DEVELOPMENT) {
				connectStorageEmulator(storage, emulatorUrl, 9199);
			}
			return storage;
		}),
		provideFirestore(() => {
			const firestore = initializeFirestore(
				initializeApp(environment.firebase),
				{
					ignoreUndefinedProperties: true,
				},
			);
			if (environment.env === envTypes.DEVELOPMENT) {
				connectFirestoreEmulator(firestore, emulatorUrl, 8082);
			}

			return firestore;
		}),
		provideRemoteConfig(() => getRemoteConfig()),
		provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
		...PROVIDE_RULE_DEFINITIONS_STATE,
		...PROVIDE_CLIENT_INTAKE_RESPONSES_STATE,
		...PROVIDE_CURRENT_CLIENT_INTAKE_RESPONSE_STATE,
		...PROVIDE_INTAKE_FORM_ITEMS_STATE,
		...PROVIDE_CLIENT_INTAKE_FORM_ITEMS_STATE,
		...PROVIDE_INTAKE_FORM_CONTEXT_STATE,
		...PROVIDE_CLIENT_INTAKE_FORM_CONTEXT_STATE,
		...PROVIDE_PORTAL_ENGINE_STATE,
		themeConfig,
	],
	bootstrap: [AppComponent],
})
export class AppModule {
	constructor(
		private urlHistory: UrlHistoryService,
		private sessionService: SessionService,
	) {}
}
